// src/pages/Onboarding/slides/ImageAnalysisSlide.js

import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from 'react';
import SaveReportPopup from '../../../components/Popup/SaveReportPopup';
import InteractiveCanvas from '../../../components/Onboarding/InteractiveCanvas';
import HairDensityGradient from '../../../components/Onboarding/HairDensityGradient';
import HairMetricsBar from '../../../components/Onboarding/HairMetricsBar';
import { getImageUploadArtifact, setAnalysisArtifact } from '../../../utils/Onboarding/artifacts';
import { detectFaces } from '../../../utils/Onboarding/MediaPipe';
import {
  segmentHair,
  buildHairOverlayCanvas,
  computePrimaryHairBoundingBoxOriginal,
  getHairMaskStats
} from '../../../utils/Onboarding/HairSegmentation';
import {
  segmentSelfie,
  buildBackgroundOverlayCanvas
} from '../../../utils/Onboarding/SelfieSegmentation';
import { drawHairShimmer } from '../../../components/Onboarding/canvas/ShimmerDraw';
import { useShimmerLoop } from '../../../components/Onboarding/hooks/useShimmerLoop';
import { triggerStorageHairAnalysis } from '../../../utils/Onboarding/FirebaseHairAnalysis';
import { startHairgenJob } from '../../../utils/Onboarding/hairgenJobTracker';
import { triggerHairGeneration } from '../../../utils/Onboarding/hairgenApi';
import { sendAnalysisSMS } from '../../../utils/Onboarding/smsApi';
import { sendAnalysisEmail } from '../../../utils/Onboarding/autoMail';
import {
  extractAnalysis,
  computeNorwoodScore,
} from '../../../utils/Onboarding/ResultMapping';
import { trackEvent } from '../../../utils/Tracking';
import refreshIcon from '../../../images/refresh.svg';
import HotspotExplanationBox from '../../../components/HotspotExplanationBox';

// ========================================
// EDGE GLOW CONFIGURATION
// ========================================
// Subtle border glow around canvas that intensifies during shimmer animations.
// Rendered directly on the canvas itself for perfect sync with shimmer.
const EDGE_GLOW_CONFIG = {
  enabled: true,                    // Toggle edge glow effect
  widthPx: 3,                       // Width of the edge glow border in pixels
  activeOpacity: 0.3,               // Opacity when shimmer animation is active
  idleOpacity: 0.1,                 // Subtle glow when no animation
};

// ========================================
// BUCKET TO PERCENTAGE MAPPING
// ========================================
// Maps bucket values (none, minimal, moderate, severe, full_bald) to percentages (10%-90%)
const BUCKET_TO_PERCENT = {
  none: 10,
  minimal: 30,
  moderate: 50,
  severe: 70,
  full_bald: 90,
};

function mapBucketToPercent(bucket) {
  return BUCKET_TO_PERCENT[bucket] ?? 50; // default to 50% if unknown
}

// ========================================
// MASK CANVAS CONFIGURATION
// ========================================
const MASK_SCALE = 0.5; // 50% resolution for memory savings (~1.92MB per mask at 1600x1200)

/**
 * Create a downscaled mask canvas from MediaPipe category mask data
 * Uses nearest-neighbor sampling for performance (every 2nd pixel)
 * @param {Uint8Array} labels - Category mask labels from MediaPipe
 * @param {number} fullWidth - Full mask width in pixels
 * @param {number} fullHeight - Full mask height in pixels
 * @param {Function} isIncluded - Predicate: (label) => boolean, determines if pixel is in mask
 * @returns {HTMLCanvasElement} Canvas at MASK_SCALE resolution
 */
function createDownscaledMaskCanvas(labels, fullWidth, fullHeight, isIncluded) {
  const scaledW = Math.ceil(fullWidth * MASK_SCALE);
  const scaledH = Math.ceil(fullHeight * MASK_SCALE);
  
  const canvas = document.createElement('canvas');
  canvas.width = scaledW;
  canvas.height = scaledH;
  const ctx = canvas.getContext('2d');
  const imageData = ctx.createImageData(scaledW, scaledH);
  const data = imageData.data;
  
  // Nearest-neighbor sampling: sample every (1/MASK_SCALE)th pixel
  const step = Math.round(1 / MASK_SCALE); // e.g., 2 for 50%
  let opaqueCount = 0;
  
  for (let y = 0; y < scaledH; y++) {
    const srcY = Math.min(y * step, fullHeight - 1);
    for (let x = 0; x < scaledW; x++) {
      const srcX = Math.min(x * step, fullWidth - 1);
      const srcIdx = srcY * fullWidth + srcX;
      const dstIdx = (y * scaledW + x) * 4;
      
      if (isIncluded(labels[srcIdx])) {
        // White opaque where mask is active
        data[dstIdx] = 255;     // R
        data[dstIdx + 1] = 255; // G
        data[dstIdx + 2] = 255; // B
        data[dstIdx + 3] = 255; // A
        opaqueCount++;
      } else {
        // Fully transparent where mask is inactive
        data[dstIdx] = 0;
        data[dstIdx + 1] = 0;
        data[dstIdx + 2] = 0;
        data[dstIdx + 3] = 0;
      }
    }
  }
  
  ctx.putImageData(imageData, 0, 0);
  
  
  
  return canvas;
}

/**
 * IMAGE ANALYSIS SLIDE (PROTOTYPE / TEST LAB)
 * 
 * This is a bare-bones prototype slide to test the new InteractiveCanvas component.
 * 
 * TESTING FEATURES:
 * - Image display from artifacts
 * - MediaPipe integration (face detection, hair segmentation)
 * - Coordinate mapping visualization
 * - Zoom/crop transitions
 * - Aspect ratio changes
 * - Overlay rendering
 * - Interactive hotspots
 * 
 * TEMPORARY: This slide will be completely redesigned once canvas is proven robust.
 */

export default function ImageAnalysisSlide({
  currentStep,
  totalSteps,
  onNext,
  onBack,
  onReset = null,
  onAutoSave = null,
  theme = 'light',
  sessionId = null,
  userName = '',
  userAge = null,
  candidateScorePercent = 75,
  expectedSatisfactionPercent = 90,
}) {
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // MediaPipe results
  const [faceDetections, setFaceDetections] = useState(null);
  const [hairMask, setHairMask] = useState(null);
  const [hairBbox, setHairBbox] = useState(null);
  const [hairOverlayDataUrl, setHairOverlayDataUrl] = useState(null);
  const [hairStats, setHairStats] = useState(null);
  const [bodySegmentation, setBodySegmentation] = useState(null);
  const [bodyOverlayDataUrl, setBodyOverlayDataUrl] = useState(null);
  
  // NEW: Downscaled mask canvas refs for compositing-based rendering (no data URLs)
  // These canvases are at MASK_SCALE (50%) resolution for memory efficiency
  const bodyMaskCanvasRef = useRef(null);
  const hairMaskCanvasRef = useRef(null);
  
  // Debug mode state
  const [debugMode, setDebugMode] = useState(false);
  
  // Analysis slide debug flag - controls visibility of overlays, keypoints, etc.
  const DEBUG_FLAG = false;

  // Analysis State Machine
  // START -> BODY -> POI -> HAIR -> FINAL
  const [analysisState, setAnalysisState] = useState('START');
  
  // Remote Analysis State
  // Tracks the async remote hair analysis from Firebase Cloud Function
  const [remoteAnalysisStarted, setRemoteAnalysisStarted] = useState(false);
  const [remoteAnalysisResult, setRemoteAnalysisResult] = useState(null);
  const [remoteAnalysisError, setRemoteAnalysisError] = useState(null);
  const remoteAnalysisPromiseRef = useRef(null);
  
  // Animation mode for colored mask overlays
  // Modes: 'full' | 'non-body' | 'hair'
  const [animationMode, setAnimationMode] = useState('none');

  // Canvas mode state - default to Initial for START state
  const [canvasMode, setCanvasMode] = useState('initial'); // 'initial', 'poi-small', 'poi-big'
  
  // Hotspot interaction ready state - ONLY true when fully settled in big POI mode
  // This is the single source of truth for whether hotspot clicks should trigger focus
  // Becomes true after a delay when entering big POI mode, immediately false otherwise
  const [isHotspotInteractionReady, setIsHotspotInteractionReady] = useState(false);
  const hotspotReadyTimerRef = useRef(null);
  const HOTSPOT_READY_DELAY = 400; // ms after entering big POI mode before enabling clicks
  
  // Pending hotspot - stores ID of hotspot clicked before we're ready for interaction
  // When we become ready, we'll look up the hotspot's current screen position and trigger focus
  // Only stores the ID, not position (position changes when canvas transitions to big mode)
  const pendingHotspotRef = useRef(null); // string (hotspot ID) or null
  
  // Pull tab drawer state - only two positions: collapsed (0) or expanded (auto)
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(false);
  const isDrawerExpandedRef = useRef(false); // Ref for real-time access in callbacks
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  
  // Drawer carousel state
  const [drawerCarouselIndex, setDrawerCarouselIndex] = useState(0);
  const [carouselDragStartX, setCarouselDragStartX] = useState(0);
  const [carouselDragDelta, setCarouselDragDelta] = useState(0);
  const [isCarouselDragging, setIsCarouselDragging] = useState(false);
  const CAROUSEL_SLIDE_COUNT = 3; // Total number of slides in drawer carousel
  
  // Format display name for static title
  const displayFirstName = useMemo(() => {
    if (!userName) return '';
    const firstName = userName.trim().split(/\s+/)[0] || '';
    if (!firstName) return '';
    return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  }, [userName]);
  
  // First-time animation state
  const [isFirstTimeExpanded, setIsFirstTimeExpanded] = useState(false);
  const isFirstTimeExpandedRef = useRef(false);
  
  // Animated values for first-time appearance
  const [animatedActivePercent, setAnimatedActivePercent] = useState(10); // Start at 10% (level 1 minimum)
  const [animatedHairLossLevel, setAnimatedHairLossLevel] = useState(0); // Current animated segment (0-7)
  const [showThinningBar, setShowThinningBar] = useState(true); // Start as true, use visibility instead
  const [animatedThinningPercent, setAnimatedThinningPercent] = useState(0);
  const [showHairlineBar, setShowHairlineBar] = useState(true); // Start as true, use visibility instead
  const [animatedHairlinePercent, setAnimatedHairlinePercent] = useState(0);
  const [hasFirstAnimationRun, setHasFirstAnimationRun] = useState(false); // Track if animation has completed
  
  // Save report popup state
  const [showSaveReportPopup, setShowSaveReportPopup] = useState(false);
  const [isReportSaved, setIsReportSaved] = useState(false);
  const [showSaveHighlight, setShowSaveHighlight] = useState(false);
  
  // Get segment color based on level (green to yellow to red gradient)
  const getSegmentColor = useCallback((level, targetLevel) => {
    // Yellow: #eab308, Red: #ef4444
    const green = { r: 34, g: 197, b: 94 };
    const yellow = { r: 234, g: 179, b: 8 };
    const red = { r: 239, g: 68, b: 68 };
    
    // Interpolate based on target level (1-7)
    // Level 1-2: green, Level 3-4: yellow-ish, Level 5-7: red-ish
    let color;
    if (targetLevel <= 2) {
      color = green;
    } else if (targetLevel <= 4) {
      // Interpolate from green to yellow
      const t = (targetLevel - 1) / 3; // 0 at level 1, 1 at level 4
      color = {
        r: Math.round(green.r + (yellow.r - green.r) * t),
        g: Math.round(green.g + (yellow.g - green.g) * t),
        b: Math.round(green.b + (yellow.b - green.b) * t),
      };
    } else {
      // Interpolate from yellow to red
      const t = (targetLevel - 4) / 3; // 0 at level 4, 1 at level 7
      color = {
        r: Math.round(yellow.r + (red.r - yellow.r) * t),
        g: Math.round(yellow.g + (red.g - yellow.g) * t),
        b: Math.round(yellow.b + (red.b - yellow.b) * t),
      };
    }
    
    return `rgb(${color.r}, ${color.g}, ${color.b})`;
  }, []);
  
  // Computed target values from remote analysis result
  // These are derived from remoteAnalysisResult when available
  const analysisData = useMemo(() => {
    if (!remoteAnalysisResult) return null;
    return extractAnalysis(remoteAnalysisResult);
  }, [remoteAnalysisResult]);
  
  // Generate randomOffsets once per session (stored in ref)
  // These offsets add variation to the UI metrics (0-10 random additions)
  // Generated BEFORE analysis call so they can be stored in reportMetadata
  const randomOffsetsRef = useRef(null);
  if (!randomOffsetsRef.current) {
    randomOffsetsRef.current = {
      hairLoss: Math.floor(Math.random() * 11),   // 0-10
      thinning: Math.floor(Math.random() * 11),   // 0-10
      hairline: Math.floor(Math.random() * 11),   // 0-10
    };
  }
  const randomOffsets = randomOffsetsRef.current;
  
  // Target values computed from remote analysis (with fallbacks) + random offset
  const targetHairLossLevel = useMemo(() => {
    if (!analysisData) return 3; // default fallback
    const baseScore = computeNorwoodScore(analysisData.baldness_bucket, analysisData.hairline_bucket);
    // Convert offset to level: offset 0-10 maps to ~0-1 level addition
    const levelOffset = Math.round(randomOffsets.hairLoss / 10);
    return Math.min(7, baseScore + levelOffset); // Cap at 7
  }, [analysisData, randomOffsets.hairLoss]);
  
  const targetThinningPercent = useMemo(() => {
    if (!analysisData) return 50; // default fallback
    const basePercent = mapBucketToPercent(analysisData.baldness_bucket);
    return Math.min(90, basePercent + randomOffsets.thinning); // Cap at 90%
  }, [analysisData, randomOffsets.thinning]);
  
  const targetHairlinePercent = useMemo(() => {
    if (!analysisData) return 50; // default fallback
    const basePercent = mapBucketToPercent(analysisData.hairline_bucket);
    return Math.min(90, basePercent + randomOffsets.hairline); // Cap at 90%
  }, [analysisData, randomOffsets.hairline]);
  
  // Static title message based on hair loss level
  const drawerTitleMessage = useMemo(() => {
    const name = displayFirstName || 'User';
    if (targetHairLossLevel === 1) {
      return `${name}, you have minimal hair loss`;
    } else if (targetHairLossLevel >= 2 && targetHairLossLevel <= 3) {
      return `${name}, it seems like you're losing some hair`;
    } else if (targetHairLossLevel >= 4 && targetHairLossLevel <= 5) {
      return `${name}, analysis shows you have hair loss`;
    } else {
      return `${name}, you lost significant hair`;
    }
  }, [displayFirstName, targetHairLossLevel]);
  
  // Animation speed configuration
  const HAIR_GRADIENT_ANIMATION_DURATION = 2000; // Total duration for hair gradient animation (ms)
  const GRAY_BAR_ANIMATION_DURATION = 1000; // Duration for each gray bar to fill (ms)
  
  const DRAG_THRESHOLD = 30; // Minimum pixels to drag before triggering state change
  
  // Refs for measuring drawer height dynamically
  const drawerRef = useRef(null);
  const [drawerHeight, setDrawerHeight] = useState(180); // Start with reasonable default for collapsed state
  
  // Determine if pull tab is enabled based on canvas mode
  const isPullTabEnabled = canvasMode !== 'initial';
  
  // Canvas testing states - start with Initial mode values (3:4 aspect ratio, contain)
  const [aspectRatio, setAspectRatio] = useState(3 / 4); // Start with 3:4 for Initial mode
  const [objectFit, setObjectFit] = useState('contain'); // Start with contain for Initial mode
  const [objectPosition, setObjectPosition] = useState({ x: 0.5, y: 0.5 });
  const [scale, setScale] = useState(1.0);
  const [cropRect, setCropRect] = useState(null);
  const [showOverlays, setShowOverlays] = useState(DEBUG_FLAG);
  const [showHotspots, setShowHotspots] = useState(false); // Hotspots only shown in FINAL state
  
  // Hotspot focus effect state
  // When a hotspot is clicked, we show a spotlight effect centered on it
  const [focusedHotspotId, setFocusedHotspotId] = useState(null);
  const [focusOverlayOpacity, setFocusOverlayOpacity] = useState(0);
  const focusAnimationRef = useRef(null);
  
  // Screen position of the focused hotspot spotlight (passed from InteractiveCanvas on click)
  const [spotlightScreenPos, setSpotlightScreenPos] = useState(null);
  
  // Geometric overlay toggles (individual controls)
  const [showEyeballLine, setShowEyeballLine] = useState(false);
  const [showNormalLine, setShowNormalLine] = useState(false);
  const [showHairlinePoint, setShowHairlinePoint] = useState(true);
  const [showTopHairPoint, setShowTopHairPoint] = useState(true);
  const [showTopHairLine, setShowTopHairLine] = useState(false);
  const [showRecedingLines, setShowRecedingLines] = useState(false);
  const [showRecedingPoints, setShowRecedingPoints] = useState(true);
  const [showEarLines, setShowEarLines] = useState(false);
  const [showTempleLines, setShowTempleLines] = useState(false);
  const [showTemplePoints, setShowTemplePoints] = useState(true);
  const [showScalpSquare, setShowScalpSquare] = useState(false);
  const [showHairZoomSquare, setShowHairZoomSquare] = useState(false);

  // Canvas mode handlers
  const applyInitialMode = useCallback(() => {
setCanvasMode('initial');
    setAspectRatio(3 / 4);
    setObjectFit('contain');
    setScale(0.67);
    setCropRect(null);
  }, []);

  const applyPOISmallMode = useCallback(() => {
setCanvasMode('poi-small');
    setAspectRatio(1);
    setObjectFit('cover');
    // cropRect will be set from hairGeometry
  }, []);

  const applyPOIBigMode = useCallback(() => {
setCanvasMode('poi-big');
    setAspectRatio(1);
    setObjectFit('cover');
    // cropRect will be set from hairGeometry
  }, []);

  // Sync canvas mode with drawer state - optimized to prevent loops
  const lastDrawerStateRef = useRef(isDrawerExpanded);
  
  useEffect(() => {
    // Only process if drawer state actually changed
    if (lastDrawerStateRef.current === isDrawerExpanded) {
      return;
    }
    lastDrawerStateRef.current = isDrawerExpanded;

    if (canvasMode === 'initial') {
      // Force drawer closed in initial mode
      if (isDrawerExpanded) {
        setIsDrawerExpanded(false);
        isDrawerExpandedRef.current = false;
      }
      return;
    }

    // In POI modes, switch between Big and Small based on drawer state
    if (isDrawerExpanded && canvasMode === 'poi-big') {
applyPOISmallMode();
    } else if (!isDrawerExpanded && canvasMode === 'poi-small') {
applyPOIBigMode();
    }
  }, [isDrawerExpanded, canvasMode, applyPOISmallMode, applyPOIBigMode]);

  // Measure drawer height for dynamic canvas positioning
  // Using useLayoutEffect to measure synchronously before paint
  useLayoutEffect(() => {
    const updateDrawerHeight = () => {
      if (drawerRef.current) {
        const height = drawerRef.current.offsetHeight;
        setDrawerHeight(height);
}
    };
    
    // Measure immediately before paint
    updateDrawerHeight();
    
    // Update on window resize
    window.addEventListener('resize', updateDrawerHeight);
    
    // Use ResizeObserver for when drawer expands/collapses
    let resizeObserver;
    if (drawerRef.current) {
      resizeObserver = new ResizeObserver(updateDrawerHeight);
      resizeObserver.observe(drawerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', updateDrawerHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isDrawerExpanded]); // Re-run when drawer state changes

  // Calculate dynamic canvas positioning based on available space
  const canvasPositioning = useMemo(() => {
    const TOP_BANNERS_HEIGHT = 88; // Top banner (56px) + Sub-banner (32px)
    const MIN_VERTICAL_MARGIN = 40; // Minimum margin top + bottom
    const HORIZONTAL_PADDING = 48; // px-5 sm:px-6 = ~24px per side
    
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Drawer is ALWAYS present (button section always visible), so always account for its height
    // The drawer height changes when expandable content appears in FINAL state, but it's never 0
    const effectiveDrawerHeight = drawerHeight;
    
    // Available dimensions
    const availableHeight = viewportHeight - TOP_BANNERS_HEIGHT - effectiveDrawerHeight - MIN_VERTICAL_MARGIN;
    const maxContainerWidth = Math.min(viewportWidth - HORIZONTAL_PADDING, 672); // max-w-2xl
    
    // Canvas size depends on aspect ratio (3:4 for initial, 1:1 for POI modes)
    const currentAspectRatio = canvasMode === 'initial' ? (3 / 4) : 1;
    const naturalCanvasWidth = maxContainerWidth;
    const naturalCanvasHeight = naturalCanvasWidth / currentAspectRatio;
    
    // Check if natural size fits in available height
    let scale;
    
    if (naturalCanvasHeight <= availableHeight) {
      // Canvas fits naturally, no scaling needed
      scale = 1.0;
    } else {
      // Canvas is too tall, scale it down to fit
      scale = availableHeight / naturalCanvasHeight;
    }
    
    // Center vertically in available space
    const scaledCanvasHeight = naturalCanvasHeight * scale;
    const paddingTop = Math.max(20, (availableHeight - scaledCanvasHeight) / 2);
return {
      scale,
      paddingTop: `${paddingTop}px`,
    };
  }, [drawerHeight, analysisState, canvasMode]);

  // Load image from artifacts
  useEffect(() => {
    if (!sessionId) {
      console.warn('[ImageAnalysisSlide] No sessionId provided');
      return;
    }
const artifact = getImageUploadArtifact(sessionId);
    
    if (!artifact || !artifact.imageDataUrl) {
      console.warn('[ImageAnalysisSlide] No image found in artifacts');
      return;
    }
setImageDataUrl(artifact.imageDataUrl);
    
    // Store the Firebase storage path for remote analysis
    if (artifact.firebaseStoragePath) {
      // Store in ref for use in remote analysis trigger
      firebaseStoragePathRef.current = artifact.firebaseStoragePath;
    }
  }, [sessionId]);
  
  // Ref to store Firebase storage path for remote analysis
  const firebaseStoragePathRef = useRef(null);

  // Track if MediaPipe has already run to prevent loops
  const mediaPipeRanRef = useRef(false);
  
  // Mask version - increment this to force mask regeneration when logic changes
  const MASK_VERSION = 2; // v2: fixed body mask to use labels !== 0
  const maskVersionRef = useRef(0);

  // Run MediaPipe face detection when image loads
  const handleImageLoad = useCallback(async ({ width, height }) => {
// Only run MediaPipe once, but force re-run if mask version changed
    if (mediaPipeRanRef.current && maskVersionRef.current === MASK_VERSION) {
return;
    }
    
    // Reset if version changed
    if (maskVersionRef.current !== MASK_VERSION) {
mediaPipeRanRef.current = false;
      setBodyOverlayDataUrl(null);
      setHairOverlayDataUrl(null);
    }
    
    setImageLoaded(true);

    if (!imageDataUrl) return;

    // Mark as running to prevent loops
    mediaPipeRanRef.current = true;

    // Run MediaPipe asynchronously (don't block rendering)
    setTimeout(async () => {
      try {
        // Create temporary image element for MediaPipe
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = imageDataUrl;
        
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          setTimeout(() => reject(new Error('Timeout')), 5000);
        });
const faceResults = await detectFaces(img);
setFaceDetections(faceResults);

        // If face detected, optionally set crop (but don't auto-zoom)
        // User can click "Zoom to Face" button instead
        
        // Run hair segmentation
const hairResults = await segmentHair(img);
setHairMask(hairResults);
        
        // Build hair mask (pure white on hair, transparent elsewhere - for scan animation)
        if (hairResults.hairMask) {
          const width = hairResults.hairMask.width;
          const height = hairResults.hairMask.height;
const hairImageData = new ImageData(width, height);
          const data = hairImageData.data;
          let opaqueCount = 0;
          
          if (hairResults.maskType === 'confidence') {
            // Float32 probabilities
            const probs = hairResults.hairMask.getAsFloat32Array();
            let above05 = 0, below05 = 0;
            for (let i = 0; i < probs.length; i++) {
              const p = Math.max(0, Math.min(1, probs[i]));
              const j = i * 4;
              if (p > 0.5) {
                above05++;
                data[j] = 255;
                data[j + 1] = 255;
                data[j + 2] = 255;
                data[j + 3] = 255;
                opaqueCount++;
              } else {
                below05++;
                data[j] = 0;
                data[j + 1] = 0;
                data[j + 2] = 0;
                data[j + 3] = 0;
              }
            }
            
          } else if (hairResults.maskType === 'category') {
            // Uint8 classes: 1 = hair
            const cats = hairResults.hairMask.getAsUint8Array();
            let hairPixels = 0, nonHairPixels = 0;
            const uniqueCats = new Set();
            for (let i = 0; i < cats.length; i++) {
              uniqueCats.add(cats[i]);
              const isHair = cats[i] === 1;
              const j = i * 4;
              if (isHair) {
                hairPixels++;
                data[j] = 255;
                data[j + 1] = 255;
                data[j + 2] = 255;
                data[j + 3] = 255;
                opaqueCount++;
              } else {
                nonHairPixels++;
                data[j] = 0;
                data[j + 1] = 0;
                data[j + 2] = 0;
                data[j + 3] = 0;
              }
            }
            
          }
          
          
          
          // NEW: Create downscaled canvas for compositing
          if (hairResults.maskType === 'confidence') {
            const probs = hairResults.hairMask.getAsFloat32Array();
            // Need to create a Uint8 version for the downscaling function
            const labels = new Uint8Array(probs.length);
            for (let i = 0; i < probs.length; i++) {
              labels[i] = probs[i] > 0.5 ? 1 : 0;
            }
            hairMaskCanvasRef.current = createDownscaledMaskCanvas(
              labels,
              width,
              height,
              (label) => label === 1
            );
          } else if (hairResults.maskType === 'category') {
            const cats = hairResults.hairMask.getAsUint8Array();
            hairMaskCanvasRef.current = createDownscaledMaskCanvas(
              cats,
              width,
              height,
              (label) => label === 1 // Hair = category 1
            );
          }
// LEGACY: Create full-res canvas and data URL for backward compatibility
          const hairCanvas = document.createElement('canvas');
          hairCanvas.width = width;
          hairCanvas.height = height;
          const ctx = hairCanvas.getContext('2d');
          ctx.putImageData(hairImageData, 0, 0);
          
          setHairOverlayDataUrl(hairCanvas.toDataURL());
// Compute hair bounding box
          const artifact = getImageUploadArtifact(sessionId);
          const imgWidth = artifact?.imageWidth || img.naturalWidth;
          const imgHeight = artifact?.imageHeight || img.naturalHeight;
          
          const bbox = computePrimaryHairBoundingBoxOriginal(
            hairResults.maskType,
            hairResults.hairMask,
            imgWidth,
            imgHeight,
            hairResults.bgMask
          );
          
          if (bbox) {
setHairBbox(bbox);
          }
          
          // Get hair stats
          const stats = getHairMaskStats(hairResults.maskType, hairResults.hairMask);
setHairStats(stats);
        }
        
        // Run body segmentation for shimmer animation
const bodyResults = await segmentSelfie(img);
setBodySegmentation(bodyResults);
        
        // Build body overlay (PERSON mask - opaque on person, transparent on background)
        if (bodyResults?.categoryMask) {
          const width = bodyResults.categoryMask.width;
          const height = bodyResults.categoryMask.height;
          const labels = bodyResults.categoryMask.getAsUint8Array();
          
          if (labels && width && height) {
            // Debug: analyze the labels array
            let label0 = 0, label1 = 0, labelOther = 0;
            const uniqueLabels = new Set();
            for (let i = 0; i < labels.length; i++) {
              uniqueLabels.add(labels[i]);
              if (labels[i] === 0) label0++;
              else if (labels[i] === 1) label1++;
              else labelOther++;
            }
            
            
            // NEW: Create downscaled canvas for compositing (no pixel loops in render)
            bodyMaskCanvasRef.current = createDownscaledMaskCanvas(
              labels,
              width,
              height,
              (label) => label !== 0 // Person = any non-zero label
            );
// LEGACY: Also create full-res data URL for backward compatibility
            const personImageData = new ImageData(width, height);
            const data = personImageData.data;
            let opaqueCount = 0;
            for (let i = 0; i < labels.length; i++) {
              const isPerson = labels[i] !== 0;
              const j = i * 4;
              if (isPerson) {
                data[j] = 255;
                data[j + 1] = 255;
                data[j + 2] = 255;
                data[j + 3] = 255;
                opaqueCount++;
              } else {
                data[j] = 0;
                data[j + 1] = 0;
                data[j + 2] = 0;
                data[j + 3] = 0;
              }
            }
            
            const personCanvas = document.createElement('canvas');
            personCanvas.width = width;
            personCanvas.height = height;
            const ctx = personCanvas.getContext('2d');
            ctx.putImageData(personImageData, 0, 0);
            
            setBodyOverlayDataUrl(personCanvas.toDataURL());
}
        }
        
        // Update mask version
        maskVersionRef.current = MASK_VERSION;
        
        // All MediaPipe processing complete - ready to advance from START state
} catch (error) {
        console.error('[ImageAnalysisSlide] MediaPipe error:', error);
      }
    }, 100); // Small delay to let UI render first
  }, [imageDataUrl, sessionId]);

  // Calculate hair zoom geometry
  const hairGeometry = useMemo(() => {
    if (!faceDetections?.detections?.length || !hairBbox || !hairMask) return null;
    
    const face = faceDetections.detections[0];
    const keypoints = face.keypoints;
    if (!keypoints || keypoints.length < 6) return null;
    
    const artifact = getImageUploadArtifact(sessionId);
    const imgWidth = artifact?.imageWidth || faceDetections.imageSize?.width || 1600;
    const imgHeight = artifact?.imageHeight || faceDetections.imageSize?.height || 1200;
    
    // Get hair mask data (the actual blue filled area)
    // hairMask is an MPMask object with .hairMask property which is also an MPMask
    const maskMPMask = hairMask.hairMask; // MPMask object
    const maskWidth = hairMask.maskWidth || imgWidth;
    const maskHeight = hairMask.maskHeight || imgHeight;
    
    // Get the actual pixel data array
    const maskData = hairMask.maskType === 'confidence' 
      ? maskMPMask.getAsFloat32Array()  // Confidence values 0-1
      : maskMPMask.getAsUint8Array();    // Category values
    
    // Helper: Find where a line intersects with the hair mask pixels
    const findHairIntersection = (startX, startY, angle, findExit = false) => {
      const stepSize = 1;
      const cosAngle = Math.cos(angle);
      const sinAngle = Math.sin(angle);
      
      let currentX = startX;
      let currentY = startY;
      let insideHair = false;
      let firstHairPixel = null;
      let consecutiveHairPixels = 0;
      const minConsecutive = 3; // Require 3 consecutive hair pixels
      
      // Step along line
      for (let step = 0; step < imgHeight; step++) {
        const x = Math.round(currentX);
        const y = Math.round(currentY);
        
        // Check bounds
        if (x < 0 || x >= maskWidth || y < 0 || y >= maskHeight) {
          break;
        }
        
        // Check mask value at this pixel
        const maskIdx = y * maskWidth + x;
        const maskValue = maskData[maskIdx];
        
        // For confidence masks, values are 0-1 (use threshold 0.5)
        // For category masks, hair is typically category 1
        const isHair = hairMask.maskType === 'confidence' 
          ? maskValue > 0.5 
          : maskValue > 0;
        
        if (findExit) {
          // Looking for exit point (inside hair -> outside hair)
          if (isHair) {
            insideHair = true;
          } else if (insideHair) {
            return { x: currentX, y: currentY };
          }
        } else {
          // Looking for entry point (outside hair -> inside hair)
          if (isHair) {
            if (consecutiveHairPixels === 0) {
              firstHairPixel = { x: currentX, y: currentY };
            }
            consecutiveHairPixels++;
            
            if (consecutiveHairPixels >= minConsecutive) {
              return firstHairPixel;
            }
          } else {
            consecutiveHairPixels = 0;
            firstHairPixel = null;
          }
        }
        
        currentX += cosAngle * stepSize;
        currentY += sinAngle * stepSize;
      }
      
      return null;
    };
    
    // Convert keypoints from normalized to pixel coords
    const rightEye = { x: keypoints[0].x * imgWidth, y: keypoints[0].y * imgHeight };
    const leftEye = { x: keypoints[1].x * imgWidth, y: keypoints[1].y * imgHeight };
    const rightEar = { x: keypoints[4].x * imgWidth, y: keypoints[4].y * imgHeight };
    const leftEar = { x: keypoints[5].x * imgWidth, y: keypoints[5].y * imgHeight };
    
    // 1. Eyeball line (between eyes) - determines face roll
    const eyeballLine = {
      start: rightEye,
      end: leftEye,
      angle: Math.atan2(leftEye.y - rightEye.y, leftEye.x - rightEye.x),
    };
    
    const eyeMidpoint = {
      x: (rightEye.x + leftEye.x) / 2,
      y: (rightEye.y + leftEye.y) / 2,
    };
    
    const eyeDistance = Math.sqrt(
      Math.pow(leftEye.x - rightEye.x, 2) + Math.pow(leftEye.y - rightEye.y, 2)
    );
    
    // 2. Normal line (perpendicular to eyeball line, pointing up)
    const normalAngle = eyeballLine.angle - Math.PI / 2;
    
    // Extend normal line to find hair intersections
    const normalLineLength = imgHeight; // Make it long enough
    const normalLine = {
      start: eyeMidpoint,
      end: {
        x: eyeMidpoint.x + Math.cos(normalAngle) * normalLineLength,
        y: eyeMidpoint.y + Math.sin(normalAngle) * normalLineLength,
      },
    };
    
    // 3. Center hairline point (where normal line enters the blue hair mask area)
    const centerHairlinePoint = findHairIntersection(
      eyeMidpoint.x, 
      eyeMidpoint.y, 
      normalAngle, 
      false // find entry
    ) || { 
      x: eyeMidpoint.x + Math.cos(normalAngle) * (hairBbox.y + hairBbox.height - eyeMidpoint.y) / Math.sin(normalAngle),
      y: hairBbox.y + hairBbox.height 
    };
    
    // 4. Center top hair point (where normal line exits the blue hair mask area at top)
    const centerTopHairPoint = findHairIntersection(
      centerHairlinePoint.x,
      centerHairlinePoint.y,
      normalAngle,
      true // find exit
    ) || {
      x: eyeMidpoint.x + Math.cos(normalAngle) * (hairBbox.y - eyeMidpoint.y) / Math.sin(normalAngle),
      y: hairBbox.y
    };
    
    // 4.5. Center hair point (midpoint between hairline and top of head - for top thinning hotspot)
    const centerHairPoint = {
      x: (centerHairlinePoint.x + centerTopHairPoint.x) / 2,
      y: (centerHairlinePoint.y + centerTopHairPoint.y) / 2,
    };
    
    // 5. Top hair line (parallel to eyeball line through top hair point)
    const topHairLineLength = eyeDistance * 3;
    const topHairLine = {
      start: {
        x: centerTopHairPoint.x - Math.cos(eyeballLine.angle) * topHairLineLength / 2,
        y: centerTopHairPoint.y - Math.sin(eyeballLine.angle) * topHairLineLength / 2,
      },
      end: {
        x: centerTopHairPoint.x + Math.cos(eyeballLine.angle) * topHairLineLength / 2,
        y: centerTopHairPoint.y + Math.sin(eyeballLine.angle) * topHairLineLength / 2,
      },
    };
    
    // 6. Estimate ear positions if not detected (at eye level, one eye-distance away)
    let rEar = rightEar;
    let lEar = leftEar;
    
    // Check if ears seem valid (if too close to eyes, they're probably wrong)
    const rightEarDist = Math.sqrt(Math.pow(rightEar.x - rightEye.x, 2) + Math.pow(rightEar.y - rightEye.y, 2));
    const leftEarDist = Math.sqrt(Math.pow(leftEar.x - leftEye.x, 2) + Math.pow(leftEar.y - leftEye.y, 2));
    
    if (rightEarDist < eyeDistance * 0.5) {
      rEar = {
        x: rightEye.x - eyeDistance,
        y: eyeMidpoint.y, // At eye level
      };
    }
    
    if (leftEarDist < eyeDistance * 0.5) {
      lEar = {
        x: leftEye.x + eyeDistance,
        y: eyeMidpoint.y, // At eye level
      };
    }
    
    // 7. Receding lines and points (parallel normal lines from eye points)
    // Right receding line (parallel to normal, starting from right eye)
    const rightRecedingLine = {
      start: rightEye,
      end: {
        x: rightEye.x + Math.cos(normalAngle) * normalLineLength,
        y: rightEye.y + Math.sin(normalAngle) * normalLineLength,
      },
    };
    
    // Left receding line (parallel to normal, starting from left eye)
    const leftRecedingLine = {
      start: leftEye,
      end: {
        x: leftEye.x + Math.cos(normalAngle) * normalLineLength,
        y: leftEye.y + Math.sin(normalAngle) * normalLineLength,
      },
    };
    
    // Receding points: where receding lines enter the blue hair mask area
    const rightRecedingPoint = findHairIntersection(
      rightEye.x,
      rightEye.y,
      normalAngle,
      false // find entry
    ) || {
      x: rightEye.x + Math.cos(normalAngle) * ((hairBbox.y + hairBbox.height) - rightEye.y) / Math.sin(normalAngle),
      y: hairBbox.y + hairBbox.height,
    };
    
    const leftRecedingPoint = findHairIntersection(
      leftEye.x,
      leftEye.y,
      normalAngle,
      false // find entry
    ) || {
      x: leftEye.x + Math.cos(normalAngle) * ((hairBbox.y + hairBbox.height) - leftEye.y) / Math.sin(normalAngle),
      y: hairBbox.y + hairBbox.height,
    };
    
    // 7b. Temple points (at 2/3 point on normal line from eyeMidpoint to hairlinePoint)
    // Calculate the point at 2/3 distance from eyeMidpoint to centerHairlinePoint
    const templeLineAnchor = {
      x: eyeMidpoint.x + (centerHairlinePoint.x - eyeMidpoint.x) * (2/3),
      y: eyeMidpoint.y + (centerHairlinePoint.y - eyeMidpoint.y) * (2/3),
    };
    
    // Temple line is parallel to eyeball line, passing through the anchor point
    const templeLineLength = eyeDistance * 3;
    const templeLine = {
      start: {
        x: templeLineAnchor.x - Math.cos(eyeballLine.angle) * templeLineLength / 2,
        y: templeLineAnchor.y - Math.sin(eyeballLine.angle) * templeLineLength / 2,
      },
      end: {
        x: templeLineAnchor.x + Math.cos(eyeballLine.angle) * templeLineLength / 2,
        y: templeLineAnchor.y + Math.sin(eyeballLine.angle) * templeLineLength / 2,
      },
    };
    
    // Find temple points: starting from center anchor, go outwards to find first hair intersection
    // Right temple point (from anchor going towards right eye - opposite of eyeball line direction)
    const rightTemplePoint = findHairIntersection(
      templeLineAnchor.x,
      templeLineAnchor.y,
      eyeballLine.angle + Math.PI, // opposite of eyeball line (towards right)
      false // find entry
    ) || {
      x: templeLine.start.x,
      y: templeLine.start.y,
    };
    
    // Left temple point (from anchor going towards left eye - along eyeball line direction)
    const leftTemplePoint = findHairIntersection(
      templeLineAnchor.x,
      templeLineAnchor.y,
      eyeballLine.angle, // along eyeball line direction (towards left)
      false // find entry
    ) || {
      x: templeLine.end.x,
      y: templeLine.end.y,
    };
    
    // 8. Ear lines (parallel to normal, through ears)
    const earLineLength = imgHeight;
    const rightEarLine = {
      start: rEar,
      end: {
        x: rEar.x + Math.cos(normalAngle) * earLineLength,
        y: rEar.y + Math.sin(normalAngle) * earLineLength,
      },
    };
    
    const leftEarLine = {
      start: lEar,
      end: {
        x: lEar.x + Math.cos(normalAngle) * earLineLength,
        y: lEar.y + Math.sin(normalAngle) * earLineLength,
      },
    };
    
    // 9. POI Rectangle (bounded by eyeball line, top hair line, ear lines)
    const poiRect = {
      left: Math.min(rEar.x, rightEarLine.end.x),
      right: Math.max(lEar.x, leftEarLine.end.x),
      top: Math.min(centerTopHairPoint.y, topHairLine.start.y, topHairLine.end.y),
      bottom: Math.max(rightEye.y, leftEye.y),
    };
    
    poiRect.width = poiRect.right - poiRect.left;
    poiRect.height = poiRect.bottom - poiRect.top; // Note: bottom > top in screen coords
    
    // Scalp square: smallest axis-aligned square that fits POI rectangle
    const squareSize = Math.max(poiRect.width, poiRect.height);
    const scalpSquare = {
      centerX: (poiRect.left + poiRect.right) / 2,
      centerY: (poiRect.top + poiRect.bottom) / 2,
      width: squareSize,
      height: squareSize,
    };
    
    scalpSquare.left = scalpSquare.centerX - squareSize / 2;
    scalpSquare.top = scalpSquare.centerY - squareSize / 2;
    scalpSquare.right = scalpSquare.left + squareSize;
    scalpSquare.bottom = scalpSquare.top + squareSize;
    
    // 10. Hair zoom square (1.5x scalp square)
    const expansionFactor = 1.5;
    const hairZoomSquare = {
      x: scalpSquare.centerX - (scalpSquare.width * expansionFactor) / 2,
      y: scalpSquare.centerY - (scalpSquare.height * expansionFactor) / 2,
      width: scalpSquare.width * expansionFactor,
      height: scalpSquare.height * expansionFactor,
    };
    
    // Clamp to image bounds
    hairZoomSquare.x = Math.max(0, hairZoomSquare.x);
    hairZoomSquare.y = Math.max(0, hairZoomSquare.y);
    hairZoomSquare.width = Math.min(imgWidth - hairZoomSquare.x, hairZoomSquare.width);
    hairZoomSquare.height = Math.min(imgHeight - hairZoomSquare.y, hairZoomSquare.height);
return {
      eyeballLine,
      eyeMidpoint,
      normalLine,
      centerHairlinePoint,
      centerTopHairPoint,
      centerHairPoint,
      topHairLine,
      rightRecedingLine,
      leftRecedingLine,
      rightRecedingPoint,
      leftRecedingPoint,
      templeLine,
      rightTemplePoint,
      leftTemplePoint,
      rightEarLine,
      leftEarLine,
      estimatedRightEar: rEar,
      estimatedLeftEar: lEar,
      poiRect,
      scalpSquare,
      hairZoomSquare,
    };
  }, [faceDetections, hairBbox, hairMask, sessionId]);

  // Apply cropRect when canvas mode changes and hairGeometry is available
  useEffect(() => {
if (canvasMode === 'poi-small' || canvasMode === 'poi-big') {
      if (hairGeometry?.hairZoomSquare) {
setCropRect(hairGeometry.hairZoomSquare);
      } else {
        console.warn('[ImageAnalysisSlide] POI mode requested but hairGeometry not available');
      }
    } else if (canvasMode === 'initial') {
setCropRect(null);
    }
  }, [canvasMode, hairGeometry]);

  // Shimmer hooks removed (disabled for debugging)

  // Generate overlays from MediaPipe results
  const overlays = useMemo(() => {
    const result = [];
    
    // ============================
    // SHIMMER ANIMATION OVERLAYS - Show shimmer effect based on animation mode
    // Shimmer uses canvas-ref masks (bodyMaskCanvasRef, hairMaskCanvasRef)
    // passed directly to InteractiveCanvas - no data URLs needed here.
    // ============================
// Show shimmer overlay based on current animation mode
    let shimmerOverlay = null;
    
    // Use centralized SHIMMER_CONFIG from InteractiveCanvas
    // Supported modes: 'non-body' (background shimmer) and 'hair' (hair shimmer)
    // 'none' or any other value = no shimmer
    
    if (animationMode === 'non-body') {
      // Non-body (background only) shimmer - requires body mask canvas
      if (bodyMaskCanvasRef.current) {
        shimmerOverlay = {
          type: 'shimmer',
          mode: 'non-body',
        };
      }
    } else if (animationMode === 'hair') {
      // Hair only shimmer - requires hair mask canvas
      if (hairMaskCanvasRef.current) {
        shimmerOverlay = {
          type: 'shimmer',
          mode: 'hair',
        };
      }
    }
    
    if (shimmerOverlay) {
      result.push(shimmerOverlay);
    }
    
    // ============================
    // DEBUG OVERLAYS (Only when DEBUG_FLAG is true)
    // ============================
    
    if (!showOverlays && !DEBUG_FLAG) return result;

    // 2. HAIR SEGMENTATION OVERLAYS (render first, below face)
    if (DEBUG_FLAG && hairMask && hairOverlayDataUrl) {
      result.push({
        type: 'image',
        coords: { x: 0, y: 0 },
        imageUrl: hairOverlayDataUrl,
        // No label for image overlays - they cover the entire image
      });
    }
    
    if (DEBUG_FLAG && debugMode && hairBbox) {
      const coverageText = hairStats && typeof hairStats.coverage === 'number' 
        ? ` (${(hairStats.coverage * 100).toFixed(1)}%)`
        : '';
      
      result.push({
        type: 'rect',
        coords: {
          x: hairBbox.x,
          y: hairBbox.y,
          width: hairBbox.width,
          height: hairBbox.height,
        },
        style: {
          strokeStyle: '#3b82f6',
          lineWidth: 3,
          globalAlpha: 0.9,
          lineDash: [5, 5],
        },
        label: `Hair Region${coverageText}`,
        labelPosition: 'top',
      });
    }

    // 3. FACE DETECTION OVERLAYS (debug mode only)
    if (DEBUG_FLAG && debugMode && faceDetections?.detections) {
      faceDetections.detections.forEach((detection, idx) => {
        const bbox = detection.boundingBox;
        const confidence = detection.categories?.[0]?.score || 0;
        
        // Face bounding box
        if (bbox) {
          result.push({
            type: 'rect',
            coords: {
              x: bbox.originX,
              y: bbox.originY,
              width: bbox.width,
              height: bbox.height,
            },
            style: {
              strokeStyle: '#00ff00',
              lineWidth: 3,
              globalAlpha: 0.9,
            },
            label: `Face ${idx + 1} (${(confidence * 100).toFixed(0)}%)`,
            labelPosition: 'top',
          });
        }

        // Facial keypoints with labeled colors
        if (detection.keypoints) {
          const artifact = getImageUploadArtifact(sessionId);
          const imgWidth = artifact?.imageWidth || faceDetections.imageSize?.width || 1600;
          const imgHeight = artifact?.imageHeight || faceDetections.imageSize?.height || 1200;
          
          detection.keypoints.forEach((kp, kpIdx) => {
            const keypointInfo = [
              { label: 'R Eye', color: '#ff0000' },
              { label: 'L Eye', color: '#ff0000' },
              { label: 'Nose', color: '#00ffff' },
              { label: 'Mouth', color: '#ff00ff' },
              { label: 'R Ear', color: '#ffff00' },
              { label: 'L Ear', color: '#ffff00' },
            ][kpIdx] || { label: `KP${kpIdx}`, color: '#ffffff' };
            
            // Convert normalized coordinates (0-1) to pixel coordinates
            const pixelX = kp.x * imgWidth;
            const pixelY = kp.y * imgHeight;
            
            result.push({
              type: 'circle',
              coords: {
                x: pixelX,
                y: pixelY,
                radius: 6,
              },
              style: {
                fillStyle: keypointInfo.color,
                strokeStyle: '#ffffff',
                lineWidth: 2,
                globalAlpha: 0.9,
              },
              label: keypointInfo.label,
              labelPosition: 'right',
            });
          });
        }
      });
    }

    // 4. GEOMETRIC OVERLAYS (distinct orange color) - Only in debug mode
    if (DEBUG_FLAG && hairGeometry) {
      const geom = hairGeometry;
      const orangeColor = '#ff6600';
      
      // Eyeball line
      if (debugMode && showEyeballLine) {
        result.push({
          type: 'line',
          coords: { start: geom.eyeballLine.start, end: geom.eyeballLine.end },
          style: { strokeStyle: orangeColor, lineWidth: 2, globalAlpha: 0.9 },
          label: 'Eyeball Line',
          labelPosition: 'top',
          labelSize: 'small',
        });
      }
      
      // Normal line
      if (debugMode && showNormalLine) {
        result.push({
          type: 'line',
          coords: { start: geom.normalLine.start, end: geom.normalLine.end },
          style: { strokeStyle: orangeColor, lineWidth: 2, globalAlpha: 0.9, lineDash: [5, 5] },
          label: 'Normal',
          labelSize: 'small',
        });
      }
      
      // Center hairline point
      if (showHairlinePoint) {
        result.push({
          type: 'circle',
          coords: { x: geom.centerHairlinePoint.x, y: geom.centerHairlinePoint.y, radius: 5 },
          style: { fillStyle: orangeColor, strokeStyle: '#fff', lineWidth: 2, globalAlpha: 0.9 },
          label: 'Hairline',
          labelSize: 'small',
        });
      }
      
      // Center top hair point
      if (showTopHairPoint) {
        result.push({
          type: 'circle',
          coords: { x: geom.centerTopHairPoint.x, y: geom.centerTopHairPoint.y, radius: 5 },
          style: { fillStyle: orangeColor, strokeStyle: '#fff', lineWidth: 2, globalAlpha: 0.9 },
          label: 'Top Hair',
          labelSize: 'small',
        });
      }
      
      // Top hair line
      if (debugMode && showTopHairLine) {
        result.push({
          type: 'line',
          coords: { start: geom.topHairLine.start, end: geom.topHairLine.end },
          style: { strokeStyle: orangeColor, lineWidth: 2, globalAlpha: 0.9 },
          label: 'Top Line',
          labelSize: 'small',
        });
      }
      
      // Receding lines (parallel to normal from eye-ear midpoints)
      if (debugMode && showRecedingLines) {
        result.push({
          type: 'line',
          coords: { start: geom.rightRecedingLine.start, end: geom.rightRecedingLine.end },
          style: { strokeStyle: orangeColor, lineWidth: 2, globalAlpha: 0.9, lineDash: [5, 5] },
          label: 'R Receding',
          labelSize: 'small',
        });
        
        result.push({
          type: 'line',
          coords: { start: geom.leftRecedingLine.start, end: geom.leftRecedingLine.end },
          style: { strokeStyle: orangeColor, lineWidth: 2, globalAlpha: 0.9, lineDash: [5, 5] },
          label: 'L Receding',
          labelSize: 'small',
        });
      }
      
      // Receding points (where receding lines intersect hair)
      if (showRecedingPoints) {
        result.push({
          type: 'circle',
          coords: { x: geom.rightRecedingPoint.x, y: geom.rightRecedingPoint.y, radius: 4 },
          style: { fillStyle: orangeColor, globalAlpha: 0.9 },
          label: 'R Recede',
          labelSize: 'small',
        });
        
        result.push({
          type: 'circle',
          coords: { x: geom.leftRecedingPoint.x, y: geom.leftRecedingPoint.y, radius: 4 },
          style: { fillStyle: orangeColor, globalAlpha: 0.9 },
          label: 'L Recede',
          labelSize: 'small',
        });
      }
      
      // Ear lines
      if (debugMode && showEarLines) {
        result.push({
          type: 'line',
          coords: { start: geom.rightEarLine.start, end: geom.rightEarLine.end },
          style: { strokeStyle: orangeColor, lineWidth: 2, globalAlpha: 0.9, lineDash: [5, 5] },
        });
        
        result.push({
          type: 'line',
          coords: { start: geom.leftEarLine.start, end: geom.leftEarLine.end },
          style: { strokeStyle: orangeColor, lineWidth: 2, globalAlpha: 0.9, lineDash: [5, 5] },
        });
      }
      
      // Temple line (horizontal line across top of face bbox)
      if (debugMode && showTempleLines) {
        result.push({
          type: 'line',
          coords: { start: geom.templeLine.start, end: geom.templeLine.end },
          style: { strokeStyle: orangeColor, lineWidth: 2, globalAlpha: 0.9 },
          label: 'Temple Line',
          labelSize: 'small',
        });
      }
      
      // Temple points (where temple line intersects hair from center outwards)
      if (showTemplePoints) {
        result.push({
          type: 'circle',
          coords: { x: geom.rightTemplePoint.x, y: geom.rightTemplePoint.y, radius: 4 },
          style: { fillStyle: orangeColor, strokeStyle: '#fff', lineWidth: 2, globalAlpha: 0.9 },
          label: 'R Temple',
          labelSize: 'small',
        });
        
        result.push({
          type: 'circle',
          coords: { x: geom.leftTemplePoint.x, y: geom.leftTemplePoint.y, radius: 4 },
          style: { fillStyle: orangeColor, strokeStyle: '#fff', lineWidth: 2, globalAlpha: 0.9 },
          label: 'L Temple',
          labelSize: 'small',
        });
      }
      
      // Scalp square
      if (debugMode && showScalpSquare) {
        result.push({
          type: 'rect',
          coords: {
            x: geom.scalpSquare.left,
            y: geom.scalpSquare.top,
            width: geom.scalpSquare.width,
            height: geom.scalpSquare.height,
          },
          style: { strokeStyle: orangeColor, lineWidth: 3, globalAlpha: 0.9 },
          label: 'Scalp Square',
          labelSize: 'small',
        });
      }
      
      // Hair zoom square
      if (debugMode && showHairZoomSquare) {
        result.push({
          type: 'rect',
          coords: {
            x: geom.hairZoomSquare.x,
            y: geom.hairZoomSquare.y,
            width: geom.hairZoomSquare.width,
            height: geom.hairZoomSquare.height,
          },
          style: { strokeStyle: '#ff0000', lineWidth: 4, globalAlpha: 0.95 },
          label: 'Hair Zoom (1.5x)',
          labelPosition: 'top',
          labelSize: 'small',
        });
      }
    }

    return result;
  }, [animationMode, bodySegmentation, hairMask,
      faceDetections, hairBbox, hairStats, hairGeometry, showOverlays, debugMode,
      showEyeballLine, showNormalLine, showHairlinePoint, showTopHairPoint, showTopHairLine, 
      showRecedingLines, showRecedingPoints, showEarLines, showTempleLines, showTemplePoints, showScalpSquare, showHairZoomSquare, sessionId]);

  // ==============================================
  // STATE MACHINE LOGIC - Simplified (no scan grid animations)
  // ==============================================
  
  // Timing configuration (in milliseconds)
  const STATE_TIMING = {
    // ─────────────────────────────────────────────────────────────
    // BODY STATE TIMING
    // ─────────────────────────────────────────────────────────────
    bodyWarmUpDelay: 1000,           // Delay before body shimmer starts (prevents stuttering)
    bodyShimmerDuration: 3000,      // How long non-body shimmer shows
    
    // ─────────────────────────────────────────────────────────────
    // POI STATE TIMING (zoom transition)
    // ─────────────────────────────────────────────────────────────
    poiZoomDuration: 1500,          // Time for canvas zoom animation to complete
    
    // ─────────────────────────────────────────────────────────────
    // HAIR STATE TIMING
    // ─────────────────────────────────────────────────────────────
    hairWarmUpDelay: 500,           // Delay before hair shimmer starts (prevents stuttering)
    hairShimmerDuration: 3000,      // How long hair shimmer shows before final
    
    // ─────────────────────────────────────────────────────────────
    // FINAL STATE TIMING
    // ─────────────────────────────────────────────────────────────
    finalPulltabExpandDelay: 500,   // Delay before auto-expanding pulltab in FINAL state
  };
  
  // Animation Sequence:
  // START (wait for MediaPipe + remote analysis started) → BODY (warm-up → non-body shimmer → none) → POI (zoom, wait for remote result) → HAIR (hair shimmer) → FINAL
  
  // Effect: Trigger remote analysis when MediaPipe completes in START state
  // This fires the analysis request without waiting for the result
  useEffect(() => {
    // Only trigger once when all conditions are met in START state
    if (
      analysisState === 'START' &&
      faceDetections &&
      hairMask &&
      bodySegmentation &&
      hairGeometry &&
      !remoteAnalysisStarted
    ) {
      const fileName = firebaseStoragePathRef.current;
      
      if (!fileName) {
        // No storage path available (possibly page refresh or missing upload)
        // Mark as started anyway to allow state machine to progress
        console.warn('[REMOTE ANALYSIS] No Firebase storage path found, skipping remote analysis');
        setRemoteAnalysisStarted(true);
        setRemoteAnalysisError('No storage path available');
        return;
      }
      
      // Get image dimensions from artifacts
      const artifact = getImageUploadArtifact(sessionId);
      const imageWidth = artifact?.imageWidth || null;
      const imageHeight = artifact?.imageHeight || null;
      
      // Build reportMetadata for the analysis request
      // This data will be stored with the analysis and returned by returnReport
      const reportMetadata = {
        userName: userName || undefined,
        userAge: typeof userAge === 'number' ? userAge : undefined,
        imageWidth: imageWidth || undefined,
        imageHeight: imageHeight || undefined,
        candidateScorePercent,
        expectedSatisfactionPercent,
        randomOffsets,
        hairGeometry: hairGeometry ? {
          eyeMidpoint: hairGeometry.eyeMidpoint,
          eyeDistance: hairGeometry.eyeDistance,
          centerHairlinePoint: hairGeometry.centerHairlinePoint,
          centerTopHairPoint: hairGeometry.centerTopHairPoint,
          leftTemplePoint: hairGeometry.leftTemplePoint,
          rightTemplePoint: hairGeometry.rightTemplePoint,
          leftRecedingPoint: hairGeometry.leftRecedingPoint,
          rightRecedingPoint: hairGeometry.rightRecedingPoint,
          scaleCropRect: hairGeometry.scaleCropRect,
          poiCropRect: hairGeometry.poiCropRect,
        } : undefined,
      };
      
      // Remove undefined values
      Object.keys(reportMetadata).forEach(key => {
        if (reportMetadata[key] === undefined) delete reportMetadata[key];
      });
      
      console.log('[REMOTE ANALYSIS] Triggering with reportMetadata:', Object.keys(reportMetadata));
      
      // Mark as started immediately (don't wait for promise)
      setRemoteAnalysisStarted(true);
      
      // Store the promise for later use in POI state
      remoteAnalysisPromiseRef.current = triggerStorageHairAnalysis({ fileName, reportMetadata })
        .then((result) => {
          setRemoteAnalysisResult(result);
          return result;
        })
        .catch((error) => {
          console.error('[REMOTE ANALYSIS ERROR]', error);
          setRemoteAnalysisError(error?.message || 'Remote analysis failed');
          // Don't throw - we'll handle this gracefully
          return null;
        });
    }
  }, [analysisState, faceDetections, hairMask, bodySegmentation, hairGeometry, remoteAnalysisStarted, sessionId, userName, userAge, candidateScorePercent, expectedSatisfactionPercent, randomOffsets]);
  
  // Effect: Persist remote analysis results to Firestore when they arrive
  // This replicates the handleAnalysisComplete logic from the deprecated ImageUploadSlide
  const lastPersistedAnalysisRef = useRef(null);
  
  useEffect(() => {
    if (!remoteAnalysisResult || !onAutoSave) return;
    
    // Extract analysis data
    const analysis = extractAnalysis(remoteAnalysisResult);
    if (!analysis) return;
    
    // Deduplicate - don't persist the same analysis twice
    const fingerprint = JSON.stringify({
      b: analysis.baldness_bucket ?? null,
      h: analysis.hairline_bucket ?? null,
      c: typeof analysis.confidence === 'number' ? analysis.confidence : null,
    });
    
    if (lastPersistedAnalysisRef.current === fingerprint) return;
    lastPersistedAnalysisRef.current = fingerprint;
    
    // Compute Norwood score from analysis
    const norwoodScore = computeNorwoodScore(analysis.baldness_bucket, analysis.hairline_bucket);
    
    // Build the summary object (matching deprecated ImageUploadSlide format)
    const summary = {
      baldness_bucket: analysis.baldness_bucket ?? null,
      hairline_bucket: analysis.hairline_bucket ?? null,
      confidence: typeof analysis.confidence === 'number' ? analysis.confidence : null,
      norwood_score: norwoodScore,
      hair_color: analysis.hair_color ?? null,
      hair_type: analysis.hair_type ?? null,
      skin_type: analysis.skin_type ?? null,
      timestamp: new Date().toISOString(),
    };
    
    // Save to session artifacts for cross-slide access (e.g., ImageGenSlide needs baldness_bucket)
    setAnalysisArtifact(sessionId, {
      baldnessBucket: summary.baldness_bucket,
      hairlineBucket: summary.hairline_bucket,
      confidence: summary.confidence,
      norwoodScore: summary.norwood_score,
      hairColor: summary.hair_color,
      hairType: summary.hair_type,
      skinType: summary.skin_type,
    });
    
    // Persist to Firestore via onAutoSave
    const updates = {
      // Append-only array (for history)
      selfieAnalysisSummaries: [summary],
      // Latest snapshot fields (used by OnboardingPage for calculations)
      lastAnalysisBaldnessBucket: summary.baldness_bucket,
      lastAnalysisHairlineBucket: summary.hairline_bucket,
      lastAnalysisConfidence: summary.confidence,
      lastAnalysisNorwoodScore: summary.norwood_score,
      lastAnalysisHairColor: summary.hair_color,
      lastAnalysisHairType: summary.hair_type,
      lastAnalysisSkinType: summary.skin_type,
    };
    
    onAutoSave(updates);
    
    // Track analysis completion event
    trackEvent('onboarding_checkpoint_analysis_completed', {
      onboarding_id: sessionId || null,
      baldness_bucket: summary.baldness_bucket,
      hairline_bucket: summary.hairline_bucket,
      norwood_score: summary.norwood_score,
      confidence: summary.confidence,
    }).catch(console.error);
    
  }, [remoteAnalysisResult, onAutoSave, sessionId]);
  
  // Effect: Trigger hair generation job after analysis completes
  // This replaces the early trigger that was in UploadSlide, allowing us to pass reportMetadata
  const hairgenTriggeredRef = useRef(false);
  
  useEffect(() => {
    // Only trigger once per analysis result
    if (hairgenTriggeredRef.current) return;
    if (!remoteAnalysisResult) return;
    
    // Get the storage path
    const fileName = firebaseStoragePathRef.current;
    if (!fileName) {
      console.warn('[ImageAnalysisSlide] No storage path for hairgen trigger');
      return;
    }
    
    // Extract the analysis requestId for linking
    // Try multiple paths since API response structure can vary
    const analysisRequestId = remoteAnalysisResult?.requestId 
      || remoteAnalysisResult?.operation?.requestId
      || remoteAnalysisResult?.data?.requestId
      || null;
    
    console.log('[ImageAnalysisSlide] Extracted analysisRequestId:', analysisRequestId, 
      'from result keys:', Object.keys(remoteAnalysisResult || {}));
    
    // Get image dimensions from artifacts
    const artifact = getImageUploadArtifact(sessionId);
    const imageWidth = artifact?.imageWidth || null;
    const imageHeight = artifact?.imageHeight || null;
    
    // Build reportMetadata for the generation request
    const reportMetadata = {
      userName: userName || null,
      userAge: typeof userAge === 'number' ? userAge : null,
      candidateScorePercent,
      expectedSatisfactionPercent,
      analysisRequestId, // Links generation to this analysis
    };
    
    // Filter out null values
    Object.keys(reportMetadata).forEach(key => {
      if (reportMetadata[key] === null) delete reportMetadata[key];
    });
    
    console.log('[ImageAnalysisSlide] Triggering hair generation with metadata:', {
      fileName,
      analysisRequestId,
      hasUserName: !!userName,
      hasUserAge: typeof userAge === 'number',
    });
    
    // Mark as triggered
    hairgenTriggeredRef.current = true;
    
    // Start the hairgen job in background
    startHairgenJob(fileName, () => 
      triggerHairGeneration({ fileName, reportMetadata })
    );
    
  }, [remoteAnalysisResult, sessionId, userName, userAge, candidateScorePercent, expectedSatisfactionPercent]);
  
  // Effect: Advance from START to BODY when MediaPipe completes AND remote analysis has started
  useEffect(() => {
    if (analysisState === 'START' && faceDetections && hairMask && bodySegmentation && hairGeometry && remoteAnalysisStarted) {
// Don't start shimmer yet - wait for warm-up delay in BODY state
      setAnimationMode('none');
      setAnalysisState('BODY');
    }
  }, [analysisState, faceDetections, hairMask, bodySegmentation, hairGeometry, remoteAnalysisStarted]);
  
  // Effect: BODY state - warm-up delay, then show non-body shimmer, then stop and advance to POI
  useEffect(() => {
    if (analysisState === 'BODY') {

// Phase 1: Warm-up delay, then start non-body shimmer
      const warmUpTimer = setTimeout(() => {
setAnimationMode('non-body');
      }, STATE_TIMING.bodyWarmUpDelay);
      
      // Phase 2: Stop shimmer before zoom
      const stopShimmerTimer = setTimeout(() => {
setAnimationMode('none');
      }, STATE_TIMING.bodyWarmUpDelay + STATE_TIMING.bodyShimmerDuration);
      
      // Phase 3: Trigger zoom and advance to POI
      const advanceTimer = setTimeout(() => {
setAnalysisState('POI');
        applyPOIBigMode();
      }, STATE_TIMING.bodyWarmUpDelay + STATE_TIMING.bodyShimmerDuration + 100);
      
      return () => {
        clearTimeout(warmUpTimer);
        clearTimeout(stopShimmerTimer);
        clearTimeout(advanceTimer);
      };
    }
  }, [analysisState, applyPOIBigMode]);
  
  // Effect: POI state - wait for zoom to complete AND remote analysis result, then advance to HAIR
  useEffect(() => {
    if (analysisState === 'POI' && canvasMode === 'poi-big' && hairGeometry) {
      // Track whether we should advance (both conditions must be met)
      let zoomCompleted = false;
      let remoteCompleted = false;
      let isCancelled = false;
      
      // Function to check if we can advance
      const tryAdvance = () => {
        if (isCancelled) return;
        if (zoomCompleted && remoteCompleted) {
          setAnalysisState('HAIR');
        }
      };
      
      // Wait for zoom animation to complete
      const zoomTimer = setTimeout(() => {
        zoomCompleted = true;
        tryAdvance();
      }, STATE_TIMING.poiZoomDuration);
      
      // Wait for remote analysis result
      if (remoteAnalysisResult || remoteAnalysisError) {
        // Already have a result (success or error)
        remoteCompleted = true;
        // Note: result already logged in the trigger effect
      } else if (remoteAnalysisPromiseRef.current) {
        // Still waiting for the promise
        remoteAnalysisPromiseRef.current
          .then(() => {
            if (isCancelled) return;
            remoteCompleted = true;
            tryAdvance();
          })
          .catch(() => {
            // Error already handled in trigger effect
            if (isCancelled) return;
            remoteCompleted = true;
            tryAdvance();
          });
      } else {
        // No remote analysis was started (missing storage path) - proceed anyway
        console.warn('[REMOTE ANALYSIS] No remote analysis promise found, proceeding without it');
        remoteCompleted = true;
      }
      
      // Initial check (in case both are already ready)
      tryAdvance();
      
      return () => {
        isCancelled = true;
        clearTimeout(zoomTimer);
      };
    }
  }, [analysisState, canvasMode, hairGeometry, remoteAnalysisResult, remoteAnalysisError]);
  
  // Effect: HAIR state - warm-up delay, then show hair mask overlay, then advance to FINAL
  useEffect(() => {
    if (analysisState === 'HAIR') {

// Don't start shimmer immediately - wait for warm-up delay
      setAnimationMode('none');
      
      // Phase 1: Warm-up delay, then start hair shimmer
      const warmUpTimer = setTimeout(() => {
setAnimationMode('hair');
      }, STATE_TIMING.hairWarmUpDelay);
      
      // Phase 2: Advance to FINAL
      const advanceTimer = setTimeout(() => {
setAnalysisState('FINAL');
        // Note: Pulltab auto-expand is handled in FINAL state effect
      }, STATE_TIMING.hairWarmUpDelay + STATE_TIMING.hairShimmerDuration);
      
      return () => {
        clearTimeout(warmUpTimer);
        clearTimeout(advanceTimer);
      };
    }
  }, [analysisState]);
  
  // Effect: FINAL state - auto-expand pulltab after a short delay
  // Note: Hair shimmer continues from HAIR state (not stopped here)
  useEffect(() => {
    if (analysisState === 'FINAL') {


// Auto-expand pulltab after delay (appears collapsed first, then expands)
      const expandTimer = setTimeout(() => {
        setIsDrawerExpanded(true);
        isDrawerExpandedRef.current = true;
      }, STATE_TIMING.finalPulltabExpandDelay);
      
      return () => clearTimeout(expandTimer);
    }
  }, [analysisState]);

  // Effect: First-time drawer expansion animation sequence
  useEffect(() => {
    if (isDrawerExpanded && !isFirstTimeExpandedRef.current) {
      isFirstTimeExpandedRef.current = true;
      setIsFirstTimeExpanded(true);
      
      // Initialize: Hair segments start at 0, gray bars start at 0%
      setAnimatedHairLossLevel(0);
      setAnimatedThinningPercent(0);
      setAnimatedHairlinePercent(0);
      
      // Phase 0: Initial delay before segment animation starts
      const initialDelayTimer = setTimeout(() => {
        // Phase 1: Animate segments lighting up one by one
        let currentLevel = 0;
        const segmentDelay = HAIR_GRADIENT_ANIMATION_DURATION / targetHairLossLevel; // Time per segment
        
        const segmentInterval = setInterval(() => {
          currentLevel++;
          setAnimatedHairLossLevel(currentLevel);
          
          if (currentLevel >= targetHairLossLevel) {
            clearInterval(segmentInterval);
            // Mark first animation as complete after segments finish
            setHasFirstAnimationRun(true);
          }
        }, segmentDelay);
      }, 500); // Initial 500ms delay
      
      return () => {
        clearTimeout(initialDelayTimer);
      };
    }
  }, [isDrawerExpanded, targetHairLossLevel, HAIR_GRADIENT_ANIMATION_DURATION]);
  
  // Effect: Animate Thinning bar when slide 2 first appears
  const hasAnimatedThinningRef = useRef(false);
  useEffect(() => {
    if (drawerCarouselIndex === 1 && !hasAnimatedThinningRef.current && isDrawerExpanded) {
      hasAnimatedThinningRef.current = true;
      setAnimatedThinningPercent(0);
      
      setTimeout(() => {
        const thinningStartTime = Date.now();
        const thinningInterval = setInterval(() => {
          const thinningElapsed = Date.now() - thinningStartTime;
          const thinningProgress = Math.min(thinningElapsed / GRAY_BAR_ANIMATION_DURATION, 1);
          const currentThinning = Math.round(targetThinningPercent * thinningProgress);
          
          setAnimatedThinningPercent(currentThinning);
          
          if (thinningProgress >= 1) {
            clearInterval(thinningInterval);
            setAnimatedThinningPercent(targetThinningPercent);
          }
        }, 16); // ~60fps
      }, 100); // Small delay for visual effect
    }
  }, [drawerCarouselIndex, isDrawerExpanded, targetThinningPercent, GRAY_BAR_ANIMATION_DURATION]);
  
  // Effect: Animate Hairline bar when slide 3 first appears
  const hasAnimatedHairlineRef = useRef(false);
  useEffect(() => {
    if (drawerCarouselIndex === 2 && !hasAnimatedHairlineRef.current && isDrawerExpanded) {
      hasAnimatedHairlineRef.current = true;
      setAnimatedHairlinePercent(0);
      
      setTimeout(() => {
        const hairlineStartTime = Date.now();
        const hairlineInterval = setInterval(() => {
          const hairlineElapsed = Date.now() - hairlineStartTime;
          const hairlineProgress = Math.min(hairlineElapsed / GRAY_BAR_ANIMATION_DURATION, 1);
          const currentHairline = Math.round(targetHairlinePercent * hairlineProgress);
          
          setAnimatedHairlinePercent(currentHairline);
          
          if (hairlineProgress >= 1) {
            clearInterval(hairlineInterval);
            setAnimatedHairlinePercent(targetHairlinePercent);
          }
        }, 16); // ~60fps
      }, 100); // Small delay for visual effect
    }
  }, [drawerCarouselIndex, isDrawerExpanded, targetHairlinePercent, GRAY_BAR_ANIMATION_DURATION]);

  // ========================================
  // HOTSPOT COLOR MAPPING (1-7 scale: 1=green, 3=yellow, 7=red)
  // ========================================
  // Optional hueOffset parameter allows slight variation for duplicate severities
  const getHotspotColor = useCallback((value, hueOffset = 0) => {
    // Clamp value to 1-7
    const v = Math.max(1, Math.min(7, value));
    
    // Interpolate between green (1) -> yellow (3) -> red (7)
    let h, s, l;
    if (v <= 3) {
      // Green to yellow (1-3)
      const t = (v - 1) / 2; // 0 at 1, 1 at 3
      // Green: hsl(120, 70%, 45%) -> Yellow: hsl(50, 90%, 50%)
      h = 120 - t * 70; // 120 -> 50
      s = 70 + t * 20;  // 70% -> 90%
      l = 45 + t * 5;   // 45% -> 50%
    } else {
      // Yellow to red (3-7)
      const t = (v - 3) / 4; // 0 at 3, 1 at 7
      // Yellow: hsl(50, 90%, 50%) -> Red: hsl(0, 85%, 55%)
      h = 50 - t * 50;  // 50 -> 0
      s = 90 - t * 5;   // 90% -> 85%
      l = 50 + t * 5;   // 50% -> 55%
    }
    // Apply hue offset (clamped to avoid wrapping into opposite colors)
    h = Math.max(0, Math.min(130, h + hueOffset));
    return `hsl(${h}, ${s}%, ${l}%)`;
  }, []);

  // ========================================
  // MAP FIREBASE OBVIOUS_SIDE TO OUR KEYPOINT SIDE
  // ========================================
  // Firebase: "image_right" / "image_left" (as displayed on screen - viewer's perspective)
  // Our keypoints: Named by anatomical position (person's perspective)
  // - rightEye = keypoints[0] = person's right eye = appears on LEFT side of image (mirrored)
  // - leftEye = keypoints[1] = person's left eye = appears on RIGHT side of image (mirrored)
  // - rightRecedingPoint is based on rightEye = appears on LEFT side of image
  // - leftRecedingPoint is based on leftEye = appears on RIGHT side of image
  // 
  // Direct mapping (Firebase left/right already accounts for mirror effect)
  const mapObviousSideToKeypoint = useCallback((obviousSide) => {
    return obviousSide === 'image_right' ? 'right' : 'left';
  }, []);

  // ========================================
  // HOTSPOT PULSE ANIMATION STATE
  // ========================================
  // Tracks which hotspots are currently pulsing (flashing their severity color)
  // Controlled by JavaScript for per-cycle random timing
  const [pulsingHotspots, setPulsingHotspots] = useState({});
  
  // ========================================
  // HOTSPOTS LOGIC
  // ========================================
  // Hotspots are shown only in FINAL state
  // Maximum 3 hotspots: 1 top hair + 1 receding + 1 temple
  // Minimum 2 hotspots guaranteed (fallback to lower severity areas if needed)
  const hotspots = useMemo(() => {
    // Only show hotspots in FINAL state with valid geometry and analysis data
    if (analysisState !== 'FINAL' || !hairGeometry || !analysisData) return [];
    
    const spots = [];
    
    // Extract hotspot-related fields from analysis
    const topThinning = analysisData.top_thinning ?? 1;
    const patternReceding = analysisData.pattern_receding ?? 1;
    const patternRecedingObviousSide = analysisData.pattern_receding_obvious_side ?? 'image_right';
    const templesReceding = analysisData.temples_receding ?? 1;
    const templesRecedingObviousSide = analysisData.temples_receding_obvious_side ?? 'image_right';
    
    const HOTSPOT_THRESHOLD = 3; // Show hotspot if value > 3
    // HOTSPOT_SCREEN_SIZE: Fixed size in screen pixels (resolution-independent)
    // The actual clickable/visible area will always be this size on screen
    const HOTSPOT_SCREEN_SIZE = 36; // pixels on screen
    
    // Track which categories we've added
    let hasTopHairHotspot = false;
    let hasRecedingHotspot = false;
    let hasTempleHotspot = false;
    
    // ========================================
    // COLLECT POTENTIAL HOTSPOTS WITH SEVERITY VALUES
    // ========================================
    // First pass: collect all hotspot data without colors
    // This lets us detect duplicate severities and assign color variations
    const pendingSpots = [];
    
    // 1. Check top thinning (centerHairPoint - midpoint between hairline and top of head)
    if (topThinning > HOTSPOT_THRESHOLD) {
      const hotspotId = 'top-thinning';
      pendingSpots.push({
        id: hotspotId,
        centerX: hairGeometry.centerHairPoint.x,
        centerY: hairGeometry.centerHairPoint.y,
        screenSize: HOTSPOT_SCREEN_SIZE,
        value: topThinning,
        label: 'Top Thinning',
      });
      hasTopHairHotspot = true;
    }
    
    // 2. Check pattern receding (rightRecedingPoint or leftRecedingPoint)
    if (patternReceding > HOTSPOT_THRESHOLD) {
      const side = mapObviousSideToKeypoint(patternRecedingObviousSide);
      const point = side === 'right' 
        ? hairGeometry.rightRecedingPoint 
        : hairGeometry.leftRecedingPoint;
      const hotspotId = `pattern-receding-${side}`;
      
      pendingSpots.push({
        id: hotspotId,
        centerX: point.x,
        centerY: point.y,
        screenSize: HOTSPOT_SCREEN_SIZE,
        value: patternReceding,
        label: `Pattern Receding`,
      });
      hasRecedingHotspot = true;
    }
    
    // 3. Check temples receding (rightTemplePoint or leftTemplePoint)
    if (templesReceding > HOTSPOT_THRESHOLD) {
      const side = mapObviousSideToKeypoint(templesRecedingObviousSide);
      const point = side === 'right' 
        ? hairGeometry.rightTemplePoint 
        : hairGeometry.leftTemplePoint;
      const hotspotId = `temples-receding-${side}`;
      
      pendingSpots.push({
        id: hotspotId,
        centerX: point.x,
        centerY: point.y,
        screenSize: HOTSPOT_SCREEN_SIZE,
        value: templesReceding,
        label: `Temple Receding`,
      });
      hasTempleHotspot = true;
    }
    
    // 4. Guarantee at least 2 hotspots
    // If less than 2, add receding hotspot first (even if value <= threshold)
    if (pendingSpots.length < 2 && !hasRecedingHotspot) {
      const side = mapObviousSideToKeypoint(patternRecedingObviousSide);
      const point = side === 'right' 
        ? hairGeometry.rightRecedingPoint 
        : hairGeometry.leftRecedingPoint;
      const hotspotId = `pattern-receding-${side}`;
      
      pendingSpots.push({
        id: hotspotId,
        centerX: point.x,
        centerY: point.y,
        screenSize: HOTSPOT_SCREEN_SIZE,
        value: patternReceding,
        label: `Pattern Receding`,
      });
      hasRecedingHotspot = true;
    }
    
    // If still less than 2, add top hair hotspot (even if value <= threshold)
    if (pendingSpots.length < 2 && !hasTopHairHotspot) {
      const hotspotId = 'top-thinning';
      pendingSpots.push({
        id: hotspotId,
        centerX: hairGeometry.centerHairPoint.x,
        centerY: hairGeometry.centerHairPoint.y,
        screenSize: HOTSPOT_SCREEN_SIZE,
        value: topThinning,
        label: 'Top Thinning',
      });
      hasTopHairHotspot = true;
    }
    
    // ========================================
    // ASSIGN COLORS WITH VARIATION FOR DUPLICATES
    // ========================================
    // Count how many times each severity appears
    const severityCounts = {};
    pendingSpots.forEach(spot => {
      severityCounts[spot.value] = (severityCounts[spot.value] || 0) + 1;
    });
    
    // Track which instance of each severity we're on (for offset calculation)
    const severityIndexes = {};
    
    // Assign colors with hue offset for duplicates
    pendingSpots.forEach(spot => {
      const count = severityCounts[spot.value];
      const index = severityIndexes[spot.value] || 0;
      severityIndexes[spot.value] = index + 1;
      
      // Calculate hue offset: spread duplicates by ±8 degrees
      // Single: offset = 0
      // Two duplicates: offsets = -8, +8
      // Three duplicates: offsets = -8, 0, +8
      let hueOffset = 0;
      if (count > 1) {
        const spread = 8; // degrees of hue variation
        hueOffset = (index - (count - 1) / 2) * (2 * spread / (count - 1 || 1));
      }
      
      spots.push({
        ...spot,
        color: getHotspotColor(spot.value, hueOffset),
      });
    });
    
    return spots;
  }, [analysisState, hairGeometry, analysisData, getHotspotColor, mapObviousSideToKeypoint]);

  // ========================================
  // HOTSPOT FOCUS EFFECT HANDLERS
  // ========================================
  
  // ========================================
  // HOTSPOT PULSE ANIMATION CONTROLLER
  // ========================================
  // Controls when each hotspot pulses in left-to-right sequence
  // Leftmost hotspot pulses first, then each subsequent one with delay
  // 
  // ─────────────────────────────────────────────────────────────
  // PULSE TIMING CONFIGURATION (easily adjustable)
  // ─────────────────────────────────────────────────────────────
  const PULSE_CONFIG = useMemo(() => ({
    // Shimmer sync
    period: 1800,              // ms - matches shimmer period
    midpoint: 900,             // ms - when first pulse starts (center of sweep)
    sequentialDelay: 100,      // ms - delay between each hotspot (left to right)
    
    // Pulse timing (total visible time = colorDuration)
    colorDuration: 350,        // ms - how long hotspot stays in colored state
    
    // Transition durations
    whiteToColor: 120,         // ms - fade from white to severity color
    colorToWhite: 250,         // ms - fade from severity color back to white
  }), []);
  
  // Uses refs for mutable state to avoid effect re-triggering on pulsingHotspots change
  const pulseStateRef = useRef({
    cycleStart: 0,
  });
  
  useEffect(() => {
    // Sort hotspots by X position (left to right) and get their IDs
    const sortedHotspots = [...hotspots].sort((a, b) => a.centerX - b.centerX);
    const hotspotIds = sortedHotspots.map(h => h.id);
    
    // Only run in FINAL state with hotspots present, and not during focus mode
    if (analysisState !== 'FINAL' || hotspotIds.length === 0 || focusedHotspotId) {
      // Clear any pulsing state when conditions not met
      setPulsingHotspots({});
      return;
    }
    
    const { period: PERIOD, colorDuration: PULSE_DURATION, midpoint: MIDPOINT, sequentialDelay: SEQUENTIAL_DELAY } = PULSE_CONFIG;
    
    // Generate pulse times: leftmost first, then sequential
    // First hotspot at MIDPOINT, each subsequent one 100ms later
    const pulseTimes = hotspotIds.map((_, index) => MIDPOINT + (index * SEQUENTIAL_DELAY));
    
    // Initialize state
    const state = pulseStateRef.current;
    state.cycleStart = performance.now();
    
    let isRunning = true;
    
    const tick = () => {
      if (!isRunning) return;
      
      const now = performance.now();
      const elapsed = now - state.cycleStart;
      
      // Check if we've entered a new cycle
      if (elapsed >= PERIOD) {
        // Reset for new cycle
        state.cycleStart = now - (elapsed % PERIOD);
        // Clear all pulses at cycle boundary
        setPulsingHotspots({});
      }
      
      const cycleTime = (now - state.cycleStart) % PERIOD;
      
      // Build new pulsing state for this tick
      const newPulsingState = {};
      
      hotspotIds.forEach((id, i) => {
        const pulseTime = pulseTimes[i];
        const pulseEndTime = pulseTime + PULSE_DURATION;
        
        // Check if we're within the pulse window
        if (cycleTime >= pulseTime && cycleTime < pulseEndTime) {
          newPulsingState[id] = true;
        }
      });
      
      // Update state (React will batch these efficiently)
      setPulsingHotspots(newPulsingState);
    };
    
    // Run at ~30fps for efficiency
    const intervalId = setInterval(tick, 33);
    tick(); // Initial call
    
    return () => {
      isRunning = false;
      clearInterval(intervalId);
      setPulsingHotspots({});
    };
  }, [analysisState, hotspots, focusedHotspotId, PULSE_CONFIG]); // Need full hotspots for X positions

  // Animate focus overlay opacity
  const animateFocusOverlay = useCallback((targetOpacity, duration = 300) => {
    if (focusAnimationRef.current) {
      cancelAnimationFrame(focusAnimationRef.current);
    }
    
    const startOpacity = focusOverlayOpacity;
    const startTime = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const newOpacity = startOpacity + (targetOpacity - startOpacity) * eased;
      
      setFocusOverlayOpacity(newOpacity);
      
      if (progress < 1) {
        focusAnimationRef.current = requestAnimationFrame(animate);
      } else {
        focusAnimationRef.current = null;
        // If fading out completely, clear focus state
        if (targetOpacity === 0) {
          setFocusedHotspotId(null);
          setSpotlightScreenPos(null);
        }
      }
    };
    
    focusAnimationRef.current = requestAnimationFrame(animate);
  }, [focusOverlayOpacity]);
  
  // Trigger focus effect on a hotspot
  const triggerHotspotFocus = useCallback((hotspotId, screenPosition) => {
    // Stop any ongoing shimmer animation during focus
    setAnimationMode('none');
    
    // Set the focused hotspot and screen position, then animate overlay in
    setFocusedHotspotId(hotspotId);
    setSpotlightScreenPos(screenPosition);
    animateFocusOverlay(0.8, 300); // Fade to 80% opacity over 300ms
  }, [animateFocusOverlay]);
  
  // Dismiss focus effect and restore shimmer
  const dismissHotspotFocus = useCallback(() => {
    if (focusedHotspotId || focusOverlayOpacity > 0) {
      animateFocusOverlay(0, 300); // Fade out over 300ms
      // Restore hair shimmer in FINAL state
      if (analysisState === 'FINAL') {
        setAnimationMode('hair');
      }
      // Note: focusedHotspotId and spotlightScreenPos will be cleared when animation completes
    }
  }, [focusedHotspotId, focusOverlayOpacity, animateFocusOverlay, analysisState]);
  
  // Handle hotspot click
  // ROBUST APPROACH: Only trigger focus if isHotspotInteractionReady is true
  // This state is managed by a dedicated effect that ensures we're fully settled in big POI mode
  const handleHotspotClick = useCallback((id, screenPosition) => {
    // If not ready for hotspot interaction, store ID as pending and collapse drawer
    if (!isHotspotInteractionReady) {
      // Store only the hotspot ID - position will be recalculated when we're ready
      pendingHotspotRef.current = id;
      
      // Collapse drawer if expanded (this will trigger transition to big POI mode)
      if (isDrawerExpandedRef.current) {
        setIsDrawerExpanded(false);
        isDrawerExpandedRef.current = false;
      }
      return;
    }
    
    // We are in settled big POI mode - trigger focus effect immediately
    triggerHotspotFocus(id, screenPosition);
  }, [isHotspotInteractionReady, triggerHotspotFocus]);
  
  // Effect: Manage hotspot interaction ready state
  // Only becomes true when: FINAL state + big POI mode + drawer collapsed + settled (after delay)
  useEffect(() => {
    // Clear any pending timer
    if (hotspotReadyTimerRef.current) {
      clearTimeout(hotspotReadyTimerRef.current);
      hotspotReadyTimerRef.current = null;
    }
    
    // Check if all conditions for big POI mode are met
    const shouldBeReady = (
      analysisState === 'FINAL' &&
      canvasMode === 'poi-big' &&
      !isDrawerExpanded
    );
    
    if (shouldBeReady) {
      // Start timer to enable hotspot interaction after animations settle
      hotspotReadyTimerRef.current = setTimeout(() => {
        setIsHotspotInteractionReady(true);
        
        // Check if there's a pending hotspot to trigger
        // We need to find the hotspot button in the DOM and simulate a click to get accurate screen position
        if (pendingHotspotRef.current) {
          const pendingId = pendingHotspotRef.current;
          pendingHotspotRef.current = null; // Clear pending
          
          // Additional 500ms delay to ensure canvas is fully settled for accurate positioning
          setTimeout(() => {
            // Find the hotspot button by its data-hotspot-id attribute
            const hotspotButton = document.querySelector(`button[data-hotspot-id="${pendingId}"]`);
            if (hotspotButton) {
              // Get the current screen position of the hotspot
              const rect = hotspotButton.getBoundingClientRect();
              const screenX = rect.left + rect.width / 2;
              const screenY = rect.top + rect.height / 2;
              const screenRadius = Math.max(rect.width, rect.height) / 2;
              
              // Find the hotspot data to get color
              const hotspotData = hotspots.find(h => h.id === pendingId);
              
              triggerHotspotFocus(pendingId, {
                screenX,
                screenY,
                screenRadius,
                color: hotspotData?.color || '#ffffff',
              });
            }
          }, 500);
        }
      }, HOTSPOT_READY_DELAY);
    } else {
      // Immediately disable hotspot interaction when conditions not met
      setIsHotspotInteractionReady(false);
      // Note: Don't clear pendingHotspotRef here - we want to preserve it
      // so when we eventually become ready, we can trigger it
    }
    
    return () => {
      if (hotspotReadyTimerRef.current) {
        clearTimeout(hotspotReadyTimerRef.current);
      }
    };
  }, [analysisState, canvasMode, isDrawerExpanded, HOTSPOT_READY_DELAY, triggerHotspotFocus, hotspots]);
  
  // Effect: End focus effect if drawer expands or canvas goes to small mode
  useEffect(() => {
    // Focus effect is incompatible with small mode or expanded drawer
    if ((isDrawerExpanded || canvasMode === 'poi-small') && focusedHotspotId) {
      // Immediately end focus effect
      dismissHotspotFocus();
    }
  }, [isDrawerExpanded, canvasMode, focusedHotspotId, dismissHotspotFocus]);
  
  // Handle canvas click to dismiss focus
  const handleCanvasClick = useCallback((e) => {
    // Only dismiss if focus is active
    if (focusedHotspotId && focusOverlayOpacity > 0) {
      e.stopPropagation();
      dismissHotspotFocus();
    }
  }, [focusedHotspotId, focusOverlayOpacity, dismissHotspotFocus]);
  
  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (focusAnimationRef.current) {
        cancelAnimationFrame(focusAnimationRef.current);
      }
    };
  }, []);
  
  // Helper function to create hotspot icon with pulse state
  // Uses PULSE_CONFIG for transition durations
  const createHotspotIcon = useCallback((color, isPulsing) => {
    // Transition duration depends on direction:
    // - When pulsing (white → color): use whiteToColor duration
    // - When not pulsing (color → white): use colorToWhite duration
    const transitionDuration = isPulsing ? PULSE_CONFIG.whiteToColor : PULSE_CONFIG.colorToWhite;
    const transition = `background-color ${transitionDuration}ms ease-out`;
    const textTransition = `color ${transitionDuration}ms ease-out`;
    
    return (
      <div 
        className="w-full h-full rounded-full flex items-center justify-center"
        style={{ 
          backgroundColor: isPulsing ? color : 'white',
          padding: '3px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          transition,
        }}
      >
        <div 
          className="w-full h-full rounded-full flex items-center justify-center"
          style={{ 
            backgroundColor: color,
            padding: '3px',
          }}
        >
          <div 
            className="w-full h-full rounded-full flex items-center justify-center"
            style={{ 
              backgroundColor: isPulsing ? color : 'white',
              transition,
            }}
          >
            <span style={{ 
              color: isPulsing ? 'white' : '#6B7280',
              fontSize: '1.5em',
              fontWeight: '2000',
              lineHeight: 1,
              transition: textTransition,
            }}>?</span>
          </div>
        </div>
      </div>
    );
  }, [PULSE_CONFIG]);
  
  // Hotspots to pass to canvas - empty during focus effect, includes pulse state
  const visibleHotspots = useMemo(() => {
    // Hide all hotspots during focus effect
    if (focusedHotspotId && focusOverlayOpacity > 0) {
      return [];
    }
    // Add icon with current pulse state to each hotspot
    return hotspots.map(hotspot => ({
      ...hotspot,
      icon: createHotspotIcon(hotspot.color, pulsingHotspots[hotspot.id] || false),
    }));
  }, [hotspots, focusedHotspotId, focusOverlayOpacity, pulsingHotspots, createHotspotIcon]);

  // Get the focused hotspot's severity value for the explanation box
  const focusedHotspotSeverity = useMemo(() => {
    if (!focusedHotspotId) return 4; // default
    const hotspot = hotspots.find(h => h.id === focusedHotspotId);
    return hotspot?.value ?? 4;
  }, [focusedHotspotId, hotspots]);

  // Calculate bounds for explanation box positioning
  // Ensures box doesn't overlap with top banners or drawer
  const explanationBoxBounds = useMemo(() => {
    const TOP_BANNERS_HEIGHT = 88; // Top banner (56px) + Sub-banner (32px)
    const DRAWER_SAFE_MARGIN = 20; // Extra margin above drawer
    
    return {
      top: TOP_BANNERS_HEIGHT,
      bottom: drawerHeight + DRAWER_SAFE_MARGIN,
      left: 16,
      right: 16,
    };
  }, [drawerHeight]);

  // Test controls - only visible when DEBUG_FLAG is true
  const testControls = DEBUG_FLAG ? (
    <div className="mt-4 p-4 bg-gray-100 rounded-lg space-y-3">
      <div className="text-sm font-semibold mb-2">🧪 Canvas Testing Controls</div>
      
      {/* State Machine Controls */}
      <div className="p-3 bg-red-50 border border-red-200 rounded">
        <label className="text-sm font-medium text-red-900">State Machine:</label>
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => {
              // Reset all animation and state machine states
              setAnalysisState('START');
              setAnimationMode('none');
              setShowHotspots(false);
              applyInitialMode();
}}
            className="px-4 py-2 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
          >
            🔄 Reset to START
          </button>
          <div className="text-xs text-red-700 flex items-center gap-4">
            <div>State: <span className="font-bold ml-1">{analysisState}</span></div>
            <div>Mode: <span className="font-bold ml-1">{animationMode}</span></div>
          </div>
        </div>
      </div>
      
      {/* Canvas Modes */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded">
        <label className="text-sm font-medium text-blue-900">Canvas Modes (POI = Point of Interest):</label>
        <div className="flex gap-2 mt-2">
          <button
            onClick={applyInitialMode}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              canvasMode === 'initial' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-white text-gray-700 hover:bg-blue-100 border border-gray-300'
            }`}
          >
            Initial (3:4)
          </button>
          <button
            onClick={applyPOISmallMode}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              canvasMode === 'poi-small' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-white text-gray-700 hover:bg-blue-100 border border-gray-300'
            }`}
            disabled={!hairGeometry}
          >
            POI Small (1:1 @ 75%)
          </button>
          <button
            onClick={applyPOIBigMode}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              canvasMode === 'poi-big' 
                ? 'bg-blue-600 text-white shadow-md' 
                : 'bg-white text-gray-700 hover:bg-blue-100 border border-gray-300'
            }`}
            disabled={!hairGeometry}
          >
            POI Big (1:1 @ 100%)
          </button>
        </div>
        <div className="text-xs text-blue-700 mt-2">
          POI modes zoom to calculated hair region. Requires face detection + hair segmentation.
        </div>
      </div>

      {/* Geometric Overlays (Individual Toggles) */}
      <div className="p-3 bg-orange-50 border border-orange-200 rounded">
        <div className="text-sm font-medium text-orange-900 mb-2">Geometric Overlays (Hair Zoom Calculation):</div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showEyeballLine}
              onChange={(e) => setShowEyeballLine(e.target.checked)}
            />
            <span>Eyeball Line</span>
          </label>
          
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showNormalLine}
              onChange={(e) => setShowNormalLine(e.target.checked)}
            />
            <span>Normal Line</span>
          </label>
          
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showHairlinePoint}
              onChange={(e) => setShowHairlinePoint(e.target.checked)}
            />
            <span>Hairline Point</span>
          </label>
          
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showTopHairPoint}
              onChange={(e) => setShowTopHairPoint(e.target.checked)}
            />
            <span>Top Hair Point</span>
          </label>
          
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showTopHairLine}
              onChange={(e) => setShowTopHairLine(e.target.checked)}
            />
            <span>Top Hair Line</span>
          </label>
          
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showRecedingLines}
              onChange={(e) => setShowRecedingLines(e.target.checked)}
            />
            <span>Receding Lines</span>
          </label>
          
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showRecedingPoints}
              onChange={(e) => setShowRecedingPoints(e.target.checked)}
            />
            <span>Receding Points</span>
          </label>
          
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showEarLines}
              onChange={(e) => setShowEarLines(e.target.checked)}
            />
            <span>Ear Lines</span>
          </label>
          
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showTempleLines}
              onChange={(e) => setShowTempleLines(e.target.checked)}
            />
            <span>Temple Lines</span>
          </label>
          
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showTemplePoints}
              onChange={(e) => setShowTemplePoints(e.target.checked)}
            />
            <span>Temple Points</span>
          </label>
          
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showScalpSquare}
              onChange={(e) => setShowScalpSquare(e.target.checked)}
            />
            <span>Scalp Square</span>
          </label>
          
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showHairZoomSquare}
              onChange={(e) => setShowHairZoomSquare(e.target.checked)}
            />
            <span className="font-medium text-red-700">Hair Zoom Square</span>
          </label>
        </div>
        <div className="flex gap-2 mt-2">
          <button
            onClick={() => {
              setShowEyeballLine(true);
              setShowNormalLine(true);
              setShowHairlinePoint(true);
              setShowTopHairPoint(true);
              setShowTopHairLine(true);
              setShowRecedingLines(true);
              setShowRecedingPoints(true);
              setShowEarLines(true);
              setShowTempleLines(true);
              setShowTemplePoints(true);
              setShowScalpSquare(true);
              setShowHairZoomSquare(true);
            }}
            className="px-2 py-1 bg-white rounded text-xs hover:bg-orange-100 border border-orange-300"
          >
            Show All
          </button>
          <button
            onClick={() => {
              setShowEyeballLine(false);
              setShowNormalLine(false);
              setShowHairlinePoint(false);
              setShowTopHairPoint(false);
              setShowTopHairLine(false);
              setShowRecedingLines(false);
              setShowRecedingPoints(false);
              setShowEarLines(false);
              setShowTempleLines(false);
              setShowTemplePoints(false);
              setShowScalpSquare(false);
              setShowHairZoomSquare(false);
            }}
            className="px-2 py-1 bg-white rounded text-xs hover:bg-orange-100 border border-orange-300"
          >
            Hide All
          </button>
        </div>
      </div>
      
      {/* Aspect Ratio */}
      <div>
        <label className="text-sm font-medium">Aspect Ratio:</label>
        <div className="flex gap-2 mt-1">
          <button
            onClick={() => setAspectRatio(1)}
            className="px-3 py-1 bg-white rounded text-sm hover:bg-gray-200"
          >
            1:1
          </button>
          <button
            onClick={() => setAspectRatio(3 / 4)}
            className="px-3 py-1 bg-white rounded text-sm hover:bg-gray-200"
          >
            3:4
          </button>
          <button
            onClick={() => setAspectRatio(16 / 9)}
            className="px-3 py-1 bg-white rounded text-sm hover:bg-gray-200"
          >
            16:9
          </button>
          <button
            onClick={() => setAspectRatio(2 / 1)}
            className="px-3 py-1 bg-white rounded text-sm hover:bg-gray-200"
          >
            2:1
          </button>
        </div>
      </div>

      {/* Canvas Scale */}
      <div>
        <label className="text-sm font-medium">Canvas Scale:</label>
        <div className="flex gap-2 mt-1">
          <button
            onClick={() => setScale(1.0)}
            className={`px-3 py-1 rounded text-sm ${scale === 1.0 ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-200'}`}
          >
            100%
          </button>
          <button
            onClick={() => setScale(0.75)}
            className={`px-3 py-1 rounded text-sm ${scale === 0.75 ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-200'}`}
          >
            75%
          </button>
          <button
            onClick={() => setScale(2 / 3)}
            className={`px-3 py-1 rounded text-sm ${Math.abs(scale - 2/3) < 0.01 ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-200'}`}
          >
            67%
          </button>
          <button
            onClick={() => setScale(0.5)}
            className={`px-3 py-1 rounded text-sm ${scale === 0.5 ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-200'}`}
          >
            50%
          </button>
        </div>
      </div>

      {/* Object Fit */}
      <div>
        <label className="text-sm font-medium">Image Fit Mode:</label>
        <div className="flex gap-2 mt-1">
          <button
            onClick={() => setObjectFit('contain')}
            className={`px-3 py-1 rounded text-sm ${objectFit === 'contain' ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-200'}`}
          >
            Contain
          </button>
          <button
            onClick={() => setObjectFit('cover')}
            className={`px-3 py-1 rounded text-sm ${objectFit === 'cover' ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-200'}`}
          >
            Cover
          </button>
          <button
            onClick={() => setObjectFit('fill')}
            className={`px-3 py-1 rounded text-sm ${objectFit === 'fill' ? 'bg-blue-500 text-white' : 'bg-white hover:bg-gray-200'}`}
          >
            Fill
          </button>
        </div>
      </div>

      {/* Object Position (for cover mode) */}
      {objectFit === 'cover' && (
        <div className="border-l-2 border-blue-300 pl-3">
          <label className="text-sm font-medium">Object Position (Cover Mode):</label>
          <div className="flex gap-3 mt-1 items-center">
            <div>
              <label className="text-xs text-gray-600">X (0=left, 1=right):</label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={objectPosition.x}
                onChange={(e) => setObjectPosition(prev => ({ ...prev, x: parseFloat(e.target.value) }))}
                className="w-20 px-2 py-1 text-sm border rounded"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">Y (0=top, 1=bottom):</label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={objectPosition.y}
                onChange={(e) => setObjectPosition(prev => ({ ...prev, y: parseFloat(e.target.value) }))}
                className="w-20 px-2 py-1 text-sm border rounded"
              />
            </div>
            <button
              onClick={() => setObjectPosition({ x: 0.5, y: 0.5 })}
              className="px-2 py-1 bg-white rounded text-xs hover:bg-gray-200"
            >
              Center
            </button>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            Tip: Use this to show specific regions (e.g., y=0.15 to show hairline)
          </div>
        </div>
      )}

      {/* Zoom */}
      <div>
        <label className="text-sm font-medium">Zoom:</label>
        <div className="flex gap-2 mt-1">
          <button
            onClick={() => setCropRect(null)}
            className="px-3 py-1 bg-white rounded text-sm hover:bg-gray-200"
          >
            Reset
          </button>
          <button
            onClick={() => {
              if (faceDetections?.detections?.length > 0) {
                const face = faceDetections.detections[0];
                const bbox = face.boundingBox;
                if (bbox) {
                  // Add padding around face
                  const padding = 80;
                  const artifact = getImageUploadArtifact(sessionId);
                  const imageWidth = artifact?.imageWidth || 1600;
                  const imageHeight = artifact?.imageHeight || 1200;
                  
                  // Calculate crop rect with padding, ensuring it stays within image bounds
                  const x = Math.max(0, bbox.originX - padding);
                  const y = Math.max(0, bbox.originY - padding);
                  const width = Math.min(imageWidth - x, bbox.width + padding * 2);
                  const height = Math.min(imageHeight - y, bbox.height + padding * 2);
setCropRect({ x, y, width, height });
                }
              }
            }}
            className="px-3 py-1 bg-white rounded text-sm hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!faceDetections?.detections?.length}
          >
            Zoom to Face {faceDetections?.detections?.length > 0 && `(${faceDetections.detections.length})`}
          </button>
        </div>
      </div>

      {/* Toggles */}
      <div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={showOverlays}
            onChange={(e) => setShowOverlays(e.target.checked)}
          />
          <span className="text-sm">Show Overlays</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={showHotspots}
            onChange={(e) => setShowHotspots(e.target.checked)}
          />
          <span className="text-sm">Show Hotspots</span>
        </label>
      </div>

      {/* Info */}
      <div className="text-xs text-gray-600 pt-2 border-t">
        <div>Faces detected: {faceDetections?.detections?.length || 0}</div>
        <div>Hair mask: {hairMask ? '✓' : '✗'}</div>
        <div>Crop: {cropRect ? `${Math.round(cropRect.x)},${Math.round(cropRect.y)} ${Math.round(cropRect.width)}×${Math.round(cropRect.height)}` : 'None'}</div>
      </div>
    </div>
  ) : null;

  // Handler to collapse drawer when canvas is interacted with
  const handleCanvasInteraction = useCallback(() => {
    if (isDrawerExpanded) {
      setIsDrawerExpanded(false);
      isDrawerExpandedRef.current = false;
    }
  }, [isDrawerExpanded]);

  // Pull tab drag handlers - immediate response when threshold crossed
  const handleDragStart = useCallback((e) => {
    if (!isPullTabEnabled) return; // Don't allow drag in initial mode
    setIsDragging(true);
    const clientY = e.type.startsWith('touch') ? e.touches[0].clientY : e.clientY;
    setDragStartY(clientY);
  }, [isPullTabEnabled]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    
    const clientY = e.type.startsWith('touch') ? e.touches[0].clientY : e.clientY;
    const deltaY = dragStartY - clientY; // Positive when dragging up, negative when dragging down
    
    // Trigger state change immediately when threshold is crossed
    if (Math.abs(deltaY) > DRAG_THRESHOLD) {
      if (deltaY > 0 && !isDrawerExpanded) {
        // Dragged up beyond threshold - expand
        setIsDrawerExpanded(true);
        isDrawerExpandedRef.current = true;
        setIsDragging(false); // Stop tracking after triggering
      } else if (deltaY < 0 && isDrawerExpanded) {
        // Dragged down beyond threshold - collapse
        setIsDrawerExpanded(false);
        isDrawerExpandedRef.current = false;
        setIsDragging(false); // Stop tracking after triggering
      }
    }
  }, [isDragging, dragStartY, isDrawerExpanded]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove event listeners for drag
  useEffect(() => {
    if (isDragging) {
      const handleMove = (e) => handleDragMove(e);
      const handleEnd = () => handleDragEnd();
      
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('touchend', handleEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Color constants for easy modification
  const colors = {
    topBanner: '#1f1f1f',
    subBanner: '#3a3a3a', // darker gray to match mockup
    subBannerText: '#9ca3af', // lighter gray text
    contentBgDark: '#3b3b3b',   // Base dark gray (bottom-left)
    contentBgLight: '#6f6f6f',  // Lighter gray (top-right)
    footerBg: '#1f1f1f',
    buttonBg: '#ffffff',
    buttonText: '#1f1f1f',
    buttonBorder: '#1f1f1f',
  };

  // Background gradient style - lighter in top-right corner
  // Darkens smoothly during focus effect by interpolating colors towards black
  // Light end darkens less to maintain gradient contrast in dark state
  // Background gradient style - lighter in top-right corner
  // Note: Focus effect darkening is now handled by full-viewport overlay, not CSS
  const backgroundGradientStyle = {
    background: `linear-gradient(135deg, ${colors.contentBgLight} 0%, ${colors.contentBgDark} 100%)`,
  };

  // Background pattern style - tileable logo pattern on dark gray
  const backgroundPatternStyle = {
    backgroundImage: `url(${process.env.PUBLIC_URL}/logo_pattern_tile.png)`,
    backgroundRepeat: 'repeat',
    backgroundSize: '340px 437px', // Fixed size, no scaling - just tile
    backgroundPosition: 'center center', // Center the tile, only tiles if viewport exceeds tile size
  };

  // Edge glow config - passed directly to InteractiveCanvas for perfect sync
  // The glow is drawn on the canvas itself, synced with the shimmer render loop
  const hasShimmerOverlay = overlays.some(o => o.type === 'shimmer');
  const edgeGlowConfig = EDGE_GLOW_CONFIG.enabled ? {
    width: EDGE_GLOW_CONFIG.widthPx,
    color: 'rgba(255, 255, 255, 1)',
    opacity: hasShimmerOverlay ? EDGE_GLOW_CONFIG.activeOpacity : EDGE_GLOW_CONFIG.idleOpacity,
  } : null;

  return (
    <div className="w-full h-screen flex flex-col relative" style={backgroundGradientStyle}>
      {/* Background Pattern Layer - Full width, behind everything */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={backgroundPatternStyle}
        aria-hidden="true"
      />
      
      {/* Top Banner - Full width black background */}
      <div className="w-full relative z-20" style={{ backgroundColor: colors.topBanner }}>
        <div className="w-full lg:w-2/5 mx-auto flex items-center justify-between px-4 py-3 sm:py-4">
          {/* Empty spacer for layout balance (back button removed) */}
          <div className="w-8 h-8" />
          <div className="flex-1 flex justify-center px-4">
            <img 
              src={require('../../../images/matchmedic_logo_white.png')} 
              alt="Match Medic" 
              className="h-6"
              style={{ maxWidth: '200px', objectFit: 'contain' }}
            />
          </div>
          <div className="w-8 h-8" />
        </div>
      </div>

      {/* Sub-Banner - Full width gray background with shadow */}
      <div 
        className="w-full -mt-1 relative z-20"
        style={{ 
          backgroundColor: colors.subBanner,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -2px rgba(0, 0, 0, 0.2)',
        }}
      >
        <div className="w-full lg:w-2/5 mx-auto px-5 sm:px-6 py-0.5 flex items-center justify-center relative">
          <span className="text-sm font-medium" style={{ color: colors.subBannerText }}>
            Quickscan™ Hair Analysis
          </span>
          {/* Refresh/Retake button - aligned right */}
          <button
            onClick={() => onReset && onReset()}
            className="absolute right-5 sm:right-6 flex items-center gap-1 hover:opacity-80 transition-opacity"
            aria-label="Retake photo"
          >
            <img 
              src={refreshIcon} 
              alt="" 
              className="h-3.5 w-3.5"
              style={{ filter: 'brightness(0) saturate(100%) invert(70%) sepia(5%) saturate(500%) hue-rotate(180deg)' }}
            />
          </button>
        </div>
      </div>

      {/* Main Content Area - Constrained width */}
      <div className={`flex-1 relative ${debugMode ? 'overflow-y-auto' : 'overflow-hidden'}`}>
        <div className="w-full lg:w-2/5 mx-auto h-full">
          <div className="w-full max-w-2xl mx-auto h-full flex items-start">
            {!imageDataUrl && (
              <div className="flex items-center justify-center h-full w-full">
                <div className="text-center text-gray-500 py-12">
                  No image found. Please go back and upload an image.
                </div>
              </div>
            )}

            {imageDataUrl && (
              <div 
                className="flex-1 flex flex-col px-5 sm:px-6"
                style={{
                  paddingTop: canvasPositioning.paddingTop,
                  transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <div 
                  className="relative flex-shrink-0"
                  style={{
                    transform: `scale(${canvasPositioning.scale})`,
                    transformOrigin: 'center top',
                    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onClick={handleCanvasInteraction}
                  onTouchStart={handleCanvasInteraction}
                >
                  <InteractiveCanvas
                    imageUrl={imageDataUrl}
                    objectFit={objectFit}
                    objectPosition={objectPosition}
                    aspectRatio={aspectRatio}
                    cropRect={cropRect}
                    overlays={overlays}
                    hotspots={visibleHotspots}
                    hotspotsClickable={!isDrawerExpanded && canvasMode === 'poi-big'}
                    onImageLoad={handleImageLoad}
                    onImageError={(error) => console.error('Canvas error:', error)}
                    onHotspotClick={handleHotspotClick}
                    theme={theme}
                    debug={debugMode}
                    // Pass mask canvas refs for compositing-based rendering
                    bodyMaskCanvas={bodyMaskCanvasRef.current}
                    hairMaskCanvas={hairMaskCanvasRef.current}
                    // Edge glow - rendered directly on canvas, synced with shimmer
                    edgeGlow={edgeGlowConfig}
                    // Focus spotlight removed - now rendered at viewport level to avoid edge seams
                  />
                  
                  {/* Debug Mode Toggle Button - Only visible when DEBUG_FLAG is true */}
                  {DEBUG_FLAG && (
                    <button
                      onClick={() => setDebugMode(!debugMode)}
                      className="absolute top-2 left-2 px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-700 transition-colors shadow-lg z-10"
                      title="Toggle debug mode"
                    >
                      {debugMode ? '🐛 Debug ON' : '🔧 Debug'}
                    </button>
                  )}
                </div>

                {debugMode && testControls}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer Container - Centered island on desktop */}
      <div className="w-full lg:w-2/5 mx-auto">
        {/* Footer with Pull Tab Drawer - Dark with White Button */}
        <div 
          ref={drawerRef}
          className="relative z-30"
          style={{ 
            backgroundColor: colors.footerBg,
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            overflow: 'hidden',
          }}
        >
        {/* Pull Tab Handle - Only visible in FINAL state */}
        {analysisState === 'FINAL' && (
          <div
            className="relative"
            style={{
              backgroundColor: colors.footerBg,
              cursor: isPullTabEnabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
              userSelect: 'none',
              touchAction: 'none',
              opacity: isPullTabEnabled ? 1 : 0.3,
              transition: 'opacity 0.3s ease',
            }}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
          >
          {/* Drag handle indicator line */}
          <div className="flex items-center justify-center py-2.5">
            <div 
              className="w-10 h-1 rounded-full" 
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.4)' }}
            />
          </div>
        </div>
        )}

        {/* Expandable Content Area - Only in FINAL state */}
        {analysisState === 'FINAL' && (
          <div
            style={{
              maxHeight: isDrawerExpanded ? '500px' : '0px',
              backgroundColor: colors.footerBg,
              transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              overflow: 'hidden',
              willChange: isDrawerExpanded ? 'max-height' : 'auto',
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
            }}
          >
            {/* Static Title - Does not change with carousel slides */}
            <div className="px-5 sm:px-6">
              <h2 className="text-white font-semibold text-lg text-center">
                {drawerTitleMessage}
              </h2>
            </div>
            
            {/* Drawer Carousel */}
            <div 
              className="relative"
              style={{ minHeight: '180px' }}
            >
              {/* Carousel Track */}
              <div
                className="flex transition-transform duration-300 ease-out"
                style={{
                  transform: `translateX(calc(-${drawerCarouselIndex * 100}% + ${carouselDragDelta}px))`,
                  transition: isCarouselDragging ? 'none' : 'transform 0.3s ease-out',
                }}
                onTouchStart={(e) => {
                  setIsCarouselDragging(true);
                  setCarouselDragStartX(e.touches[0].clientX);
                }}
                onTouchMove={(e) => {
                  if (!isCarouselDragging) return;
                  const delta = e.touches[0].clientX - carouselDragStartX;
                  setCarouselDragDelta(delta);
                }}
                onTouchEnd={() => {
                  if (!isCarouselDragging) return;
                  const threshold = 50;
                  if (carouselDragDelta > threshold && drawerCarouselIndex > 0) {
                    setDrawerCarouselIndex(drawerCarouselIndex - 1);
                  } else if (carouselDragDelta < -threshold && drawerCarouselIndex < CAROUSEL_SLIDE_COUNT - 1) {
                    setDrawerCarouselIndex(drawerCarouselIndex + 1);
                  }
                  setCarouselDragDelta(0);
                  setIsCarouselDragging(false);
                }}
                onMouseDown={(e) => {
                  setIsCarouselDragging(true);
                  setCarouselDragStartX(e.clientX);
                }}
                onMouseMove={(e) => {
                  if (!isCarouselDragging) return;
                  const delta = e.clientX - carouselDragStartX;
                  setCarouselDragDelta(delta);
                }}
                onMouseUp={() => {
                  if (!isCarouselDragging) return;
                  const threshold = 50;
                  if (carouselDragDelta > threshold && drawerCarouselIndex > 0) {
                    setDrawerCarouselIndex(drawerCarouselIndex - 1);
                  } else if (carouselDragDelta < -threshold && drawerCarouselIndex < CAROUSEL_SLIDE_COUNT - 1) {
                    setDrawerCarouselIndex(drawerCarouselIndex + 1);
                  }
                  setCarouselDragDelta(0);
                  setIsCarouselDragging(false);
                }}
                onMouseLeave={() => {
                  if (isCarouselDragging) {
                    setCarouselDragDelta(0);
                    setIsCarouselDragging(false);
                  }
                }}
              >
                {/* Slide 1: Hair Loss Level */}
                <div className="w-full flex-shrink-0 px-5 sm:px-6 py-4">
                  <div 
                    className="rounded-xl p-3"
                    style={{ background: 'linear-gradient(90deg, #353535 0%, #727272 100%)' }}
                  >
                    {/* Title */}
                    <h3 className="text-white font-semibold text-lg">
                      Hair loss level
                    </h3>
                    {/* Subtitle */}
                    <p className="text-gray-400 text-base mb-1">
                      {targetHairLossLevel} out of 7
                    </p>
                    {/* 7-Segment Indicator Bar */}
                    <div className="flex gap-1.5 mb-3">
                      {[1, 2, 3, 4, 5, 6, 7].map((level) => {
                        // Use animated level during first animation, target level after
                        const displayLevel = hasFirstAnimationRun ? targetHairLossLevel : animatedHairLossLevel;
                        const isActive = level <= displayLevel;
                        const segmentColor = isActive ? getSegmentColor(level, targetHairLossLevel) : '#6b7280';
                        
                        return (
                          <div
                            key={level}
                            className="flex-1 h-3 rounded-sm"
                            style={{
                              backgroundColor: segmentColor,
                              transition: 'background-color 0.2s ease',
                            }}
                          />
                        );
                      })}
                    </div>
                    {/* Description */}
                    <p className="text-gray-300 text-xs">
                      You would likely be classified as a Norwood {targetHairLossLevel} level hair loss patient in the Hamilton Norwood Scale.
                    </p>
                  </div>
                </div>

                {/* Slide 2: Thinning */}
                <div className="w-full flex-shrink-0 px-5 sm:px-6 py-4">
                  <div 
                    className="rounded-xl px-3 py-5"
                    style={{ background: 'linear-gradient(90deg, #763512 0%, #D38155 100%)' }}
                  >
                    {/* Title */}
                    <h3 className="text-white font-semibold text-lg">
                      Thinning
                    </h3>
                    {/* Progress Bar with Percentage */}
                    <div className="relative h-5 rounded-full overflow-hidden mb-3 mt-1" style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}>
                      <div 
                        className="absolute inset-y-0 left-0 rounded-full flex items-center"
                        style={{ 
                          width: `${animatedThinningPercent}%`,
                          backgroundColor: 'white',
                          transition: 'width 0.05s linear',
                        }}
                      >
                        <span className="text-gray-800 font-semibold text-sm pl-3">
                          {animatedThinningPercent}%
                        </span>
                      </div>
                    </div>
                    {/* Description */}
                    <p className="text-white/90 text-xs">
                      {targetThinningPercent <= 30 
                        ? "Your frontal scalp maintains excellent overall density with minimal variation."
                        : targetThinningPercent <= 50
                        ? "Your frontal scalp maintains good overall density with only subtle variation that falls within normal limits."
                        : targetThinningPercent <= 70
                        ? "Your frontal scalp shows moderate thinning that may benefit from preventive treatment."
                        : "Your frontal scalp shows significant thinning that would benefit from restoration treatment."}
                    </p>
                  </div>
                </div>

                {/* Slide 3: Hairline */}
                <div className="w-full flex-shrink-0 px-5 sm:px-6 py-4">
                  <div 
                    className="rounded-xl px-3 py-5"
                    style={{ background: 'linear-gradient(90deg, #1B6130 0%, #48AD66 100%)' }}
                  >
                    {/* Title */}
                    <h3 className="text-white font-semibold text-lg">
                      Hairline
                    </h3>
                    {/* Progress Bar with Percentage */}
                    <div className="relative h-5 rounded-full overflow-hidden mb-3 mt-1" style={{ backgroundColor: 'rgba(255, 255, 255, 0.3)' }}>
                      <div 
                        className="absolute inset-y-0 left-0 rounded-full flex items-center"
                        style={{ 
                          width: `${animatedHairlinePercent}%`,
                          backgroundColor: 'white',
                          transition: 'width 0.05s linear',
                        }}
                      >
                        <span className="text-gray-800 font-semibold text-sm pl-3">
                          {animatedHairlinePercent}%
                        </span>
                      </div>
                    </div>
                    {/* Description */}
                    <p className="text-white/90 text-xs">
                      {targetHairlinePercent <= 30 
                        ? "Your hairline is well-defined and shows minimal signs of recession."
                        : targetHairlinePercent <= 50
                        ? "Your hairline is somewhat disordered due to temporal recession."
                        : targetHairlinePercent <= 70
                        ? "Your hairline shows moderate recession that may benefit from restoration."
                        : "Your hairline shows significant recession that would benefit from transplant treatment."}
                    </p>
                  </div>
                </div>
              </div>

              {/* Carousel Dot Indicators */}
              <div className="flex justify-center gap-2 pb-2">
                {[0, 1, 2].map((index) => (
                  <button
                    key={index}
                    onClick={() => setDrawerCarouselIndex(index)}
                    className="w-2 h-2 rounded-full transition-colors duration-200"
                    style={{
                      backgroundColor: index === drawerCarouselIndex 
                        ? 'rgba(255, 255, 255, 0.9)' 
                        : 'rgba(255, 255, 255, 0.3)',
                    }}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Separator Line - Only in FINAL state */}
        {analysisState === 'FINAL' && (
          <div 
            className="h-px mx-5 sm:mx-6"
            style={{ backgroundColor: '#404040' }}
          />
        )}

        {/* Main Section - Button visible only in FINAL state, loading spinner otherwise */}
        <div className="px-5 sm:px-6 py-4">
          {analysisState === 'FINAL' ? (
            <>
              <div className="flex gap-3">
                {/* Save Report Button */}
                <button
                  onClick={() => {
                    if (!isReportSaved) {
                      setShowSaveReportPopup(true);
                    }
                  }}
                  disabled={isReportSaved}
                  className={`py-2.5 px-4 text-md font-medium rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                    isReportSaved 
                      ? 'cursor-default focus:ring-green-500' 
                      : 'hover:bg-white/10 focus:ring-white'
                  } ${showSaveHighlight ? 'save-highlight-animation' : ''}`}
                  style={{
                    backgroundColor: isReportSaved ? '#404542' : 'transparent',
                    color: isReportSaved ? 'rgba(255, 255, 255, 0.6)' : 'white',
                    border: isReportSaved ? '1px solid #404542' : '1px solid rgba(255, 255, 255, 0.5)',
                    whiteSpace: 'nowrap',
                  }}
                  onAnimationEnd={() => setShowSaveHighlight(false)}
                >
                  {isReportSaved ? 'Saved' : 'Save'}
                </button>
                
                {/* Continue Button */}
                <button
                  onClick={() => onNext && onNext(null, { imageUploadStatus: 'completed' })}
                  className="flex-1 py-2.5 px-6 text-md font-bold rounded-full transition-all duration-200 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-900"
                  style={{
                    backgroundColor: colors.buttonBg,
                    color: colors.buttonText,
                  }}
                >
                  Continue
                </button>
              </div>
              
              {/* Disclaimer text */}
              <p className="text-center text-xs text-gray-400 mt-2">
                Match Medic Quickscan™ all rights reserved.
              </p>
            </>
          ) : (
            /* Loading state - maintains same height as button + disclaimer */
            <div className="flex flex-col items-center justify-center" style={{ minHeight: '68px' }}>
              <div className="flex items-center gap-2 text-gray-400">
                {/* Rotating spinner */}
                <svg 
                  className="animate-spin h-5 w-5" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-md">Please wait...</span>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
      
      {/* Full-Viewport Focus Spotlight Overlay */}
      {/* Rendered at viewport level to ensure seamless darkening (no edge seams) */}
      {spotlightScreenPos && focusOverlayOpacity > 0 && (
        <div
          className="fixed inset-0 pointer-events-auto cursor-pointer"
          onClick={dismissHotspotFocus}
          style={{ zIndex: 50 }}
        >
          {/* SVG for sharp-edged spotlight with colored border */}
          <svg 
            width="100%" 
            height="100%" 
            className="absolute inset-0"
            style={{ pointerEvents: 'none' }}
          >
            <defs>
              <mask id="viewport-spotlight-mask">
                {/* White background = show dark overlay */}
                <rect width="100%" height="100%" fill="white" />
                {/* Black circle = transparent hole (sharp edge) */}
                <circle 
                  cx={spotlightScreenPos.screenX} 
                  cy={spotlightScreenPos.screenY} 
                  r={spotlightScreenPos.screenRadius * 2.5}
                  fill="black"
                />
              </mask>
            </defs>
            
            {/* Dark overlay with sharp circular hole */}
            <rect 
              width="100%" 
              height="100%" 
              fill="black"
              opacity={focusOverlayOpacity}
              mask="url(#viewport-spotlight-mask)"
            />
            
            {/* Colored border ring around the spotlight */}
            <circle 
              cx={spotlightScreenPos.screenX} 
              cy={spotlightScreenPos.screenY} 
              r={spotlightScreenPos.screenRadius * 2.5}
              fill="none"
              stroke={spotlightScreenPos.color || '#ffffff'}
              strokeWidth={3}
              opacity={focusOverlayOpacity > 0.5 ? 1 : focusOverlayOpacity * 2}
            />
          </svg>
          
          {/* Hotspot Explanation Box */}
          <HotspotExplanationBox
            hotspotId={focusedHotspotId}
            severity={focusedHotspotSeverity}
            spotlightPosition={{
              x: spotlightScreenPos.screenX,
              y: spotlightScreenPos.screenY,
            }}
            spotlightRadius={spotlightScreenPos.screenRadius * 2.5}
            bounds={explanationBoxBounds}
            opacity={focusOverlayOpacity > 0.5 ? 1 : focusOverlayOpacity * 2}
            onClose={dismissHotspotFocus}
          />
        </div>
      )}

      {/* Save Report Popup */}
      <SaveReportPopup
        isOpen={showSaveReportPopup}
        onClose={() => setShowSaveReportPopup(false)}
        sessionId={sessionId}
        slideNumber={5.5}
        slideName="image_analysis"
        userName={userName}
        onSaveContact={(entry) => {
          if (onAutoSave) {
            onAutoSave({ saveContacts: [entry] });
          }
        }}
        onSuccess={async ({ method, value }) => {
          console.log('[SMS System] SaveReportPopup onSuccess triggered:', { method, value: value ? `${value.slice(0, 6)}...` : null });
          
          setIsReportSaved(true);
          setShowSaveHighlight(true);
          
          // Trigger SMS if phone method was used
          if (method === 'phone') {
            console.log('[SMS System] Phone method detected, checking for analysis result...');
            console.log('[SMS System] remoteAnalysisResult exists:', !!remoteAnalysisResult);
            
            if (remoteAnalysisResult) {
              console.log('[SMS System] remoteAnalysisResult keys:', Object.keys(remoteAnalysisResult));
              
              // Extract requestId and password from analysis result
              // API responses are wrapped in 'operation' property
              const operation = remoteAnalysisResult?.operation || remoteAnalysisResult;
              const analysisRequestId = operation?.requestId || null;
              const analysisPassword = operation?.firestore?.password || null;
              
              console.log('[SMS System] Extracted credentials:', {
                analysisRequestId,
                hasPassword: !!analysisPassword,
                hasOperation: !!remoteAnalysisResult?.operation,
                operationKeys: operation ? Object.keys(operation) : null,
                firestoreKeys: operation?.firestore ? Object.keys(operation.firestore) : null,
              });
              
              if (analysisRequestId && analysisPassword) {
                try {
                  console.log('[SMS System] Calling sendAnalysisSMS...');
                  
                  // Send SMS (non-blocking - fire and forget with tracking)
                  const smsResult = await sendAnalysisSMS({
                    targetNumber: value,
                    requestId: analysisRequestId,
                    password: analysisPassword,
                    userName: userName || undefined,
                  });
                  
                  console.log('[SMS System] sendAnalysisSMS result:', smsResult);
                  
                  // Track the SMS trigger event
                  trackEvent('sms_analysis_report_triggered', {
                    onboarding_id: sessionId || null,
                    slide_number: 5.5,
                    slide_name: 'image_analysis',
                    target_number: value,
                    request_id: analysisRequestId,
                    user_name: userName || null,
                    sms_success: smsResult.success,
                    sms_message_sid: smsResult.messageSid || null,
                    sms_error: smsResult.error || null,
                    sms_duration: smsResult.duration || null,
                  }).catch(console.error);
                  
                  // Sync SMS result to Firestore
                  if (onAutoSave) {
                    onAutoSave({
                      smsAnalysisTriggers: [{
                        slideNumber: 5.5,
                        slideName: 'image_analysis',
                        targetNumber: value,
                        requestId: analysisRequestId,
                        userName: userName || null,
                        timestamp: smsResult.timestamp,
                        success: smsResult.success,
                        messageSid: smsResult.messageSid || null,
                        error: smsResult.error || null,
                        duration: smsResult.duration || null,
                      }],
                    });
                  }
                } catch (err) {
                  console.error('[SMS System] Error in SMS flow:', err);
                }
              } else {
                console.warn('[SMS System] Missing credentials - skipping SMS:', {
                  hasRequestId: !!analysisRequestId,
                  hasPassword: !!analysisPassword,
                });
                
                // Track missing credentials
                trackEvent('sms_analysis_report_skipped', {
                  onboarding_id: sessionId || null,
                  slide_number: 5.5,
                  reason: 'missing_credentials',
                  has_request_id: !!analysisRequestId,
                  has_password: !!analysisPassword,
                }).catch(console.error);
              }
            } else {
              console.warn('[SMS System] No remoteAnalysisResult available - cannot send SMS');
            }
          } else if (method === 'email') {
            console.log('[AutoMail] Email method detected, checking for analysis result...');

            if (remoteAnalysisResult) {
              const operation = remoteAnalysisResult?.operation || remoteAnalysisResult;
              const analysisRequestId = operation?.requestId || null;
              const analysisPassword = operation?.firestore?.password || null;

              console.log('[AutoMail] Extracted credentials for email:', {
                analysisRequestId,
                hasPassword: !!analysisPassword,
              });

              if (analysisRequestId && analysisPassword) {
                try {
                  const emailResult = await sendAnalysisEmail({
                    email: value,
                    requestId: analysisRequestId,
                    password: analysisPassword,
                    userName: userName || undefined,
                    sessionId,
                  });

                  console.log('[AutoMail] sendAnalysisEmail result:', emailResult);

                  if (onAutoSave) {
                    onAutoSave({
                      emailAnalysisTriggers: [{
                        slideNumber: 5.5,
                        slideName: 'image_analysis',
                        targetEmail: value,
                        requestId: analysisRequestId,
                        userName: userName || null,
                        timestamp: emailResult.timestamp,
                        success: emailResult.success,
                        messageId: emailResult.messageId || null,
                        followupsScheduled: emailResult.followupsScheduled ?? null,
                        error: emailResult.error || null,
                        duration: emailResult.duration || null,
                      }],
                    });
                  }
                } catch (err) {
                  console.error('[AutoMail] Error in email report flow:', err);
                }
              } else {
                console.warn('[AutoMail] Missing credentials - skipping report email:', {
                  hasRequestId: !!analysisRequestId,
                  hasPassword: !!analysisPassword,
                });
              }
            } else {
              console.warn('[AutoMail] No remoteAnalysisResult available - cannot send report email');
            }
          }
        }}
      />

      {/* Save button highlight animation styles */}
      <style>{`
        @keyframes saveHighlight {
          0% {
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
            border-color: #404542;
          }
          30% {
            box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.6);
            border-color: rgb(34, 197, 94);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
            border-color: #404542;
          }
        }
        .save-highlight-animation {
          animation: saveHighlight 2s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
